import { StatusBar } from 'expo-status-bar';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity, FlatList,
  Dimensions, SafeAreaView, Image, TextInput, Animated,
  Platform, Vibration,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

// Web 安全的条件 import：仅在 Native 端加载不兼容模块
// react-native-view-shot 和 expo-sharing 在 Web bundle 会崩溃，必须动态 require
const isWeb = Platform.OS === 'web';

// AsyncStorage - Web 端回退到 localStorage
let AsyncStorage: { getItem: (key: string) => Promise<string | null>; setItem: (key: string, value: string) => Promise<void> };
if (isWeb) {
  AsyncStorage = {
    getItem: async (key: string) => {
      try { return window.localStorage.getItem(key); } catch { return null; }
    },
    setItem: async (key: string, value: string) => {
      try { window.localStorage.setItem(key, value); } catch {}
    },
  };
} else {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
}

const { width, height } = Dimensions.get('window');

// ==================== 真实水晶配色（参照"珠了个珠"行业标准）====================
// 每种水晶使用真实行业标准色值，模拟天然水晶的光学质感
const CRYSTAL_COLORS: Record<string, string> = {
  '白水晶': '#F8F8FF',     // 透明冰白，带微蓝冷光，晶王本色
  '紫水晶': '#7C3AED',     // 深紫罗兰，偏蓝紫，通透感
  '黄水晶': '#D97706',     // 琥珀金橘色，天然黄晶色调
  '粉水晶': '#F9A8D4',     // 樱花粉，柔和偏淡，非浓艳
  '黑曜石': '#111827',     // 墨黑带微光，火山玻璃质感
  '绿幽灵': '#059669',     // 翠绿矿色，内含绿色幽灵体
  '金发晶': '#B45309',     // 深金棕，金丝包裹感
  '月光石': '#E8E0D0',     // 暖白微黄，月光蓝移色
  '虎眼石': '#92400E',     // 深棕金，丝光放射纹理
  '红玛瑙': '#B91C1C',     // 暗酒红，深石榴石色，非亮红
  '紫檀木': '#3B0718',     // 深紫红木色，醇厚沉稳
  '沉香木': '#422006',     // 深棕黑，沉香木油脂色
  '银隔珠': '#C0C0C0',     // 亮银色，金属光泽
  '金菩提子': '#854D0E',   // 深金棕色，菩提天然色
  // 金色配饰
  '招财纳福': '#FFD700',   // 金色招财纳福牌
  '小胖福': '#DAA520',     // 金胖福，富贵金
  '钱币': '#FFD700',       // 金色钱币
  '花丝莲花': '#FFD700',   // 金丝莲花
  '桃花': '#FF69B4',       // 粉色桃花
  '银珠': '#E8E8E8',       // 银色珠子
};

// ==================== 素材图片映射（真实水晶透明PNG）====================
// Web 端使用相对路径 URI，Expo export 后 assets 目录会被原样复制到 dist/
const CRYSTAL_IMAGES: Record<string, string> = {
  // 水晶素材
  '白水晶': '/assets/crystals/1-白水晶 White Crystal.png',
  '白幽灵': '/assets/crystals/2-白幽灵 White Phantom Quartz.png',
  '紫锂辉': '/assets/crystals/3-紫锂辉 Kunzite.png',
  '巴西紫水晶': '/assets/crystals/4-巴西紫水晶 Brazilian Amethyst.png',
  '玻利维亚紫水晶': '/assets/crystals/5-玻利维亚紫水晶 Bolivian Amethyst.png',
  '薰衣草紫水晶': '/assets/crystals/6-薰衣草紫水晶 Lavender Amethyst.png',
  '乌拉圭紫水晶': '/assets/crystals/7-乌拉圭紫水晶 Uruguayan Amethyst.png',
  '冰糖雪梨黄水晶': '/assets/crystals/8-冰糖雪梨黄水晶 Rock Candy Pear Citrine.png',
  '柠檬黄水晶': '/assets/crystals/9-柠檬黄水晶 Lemon Citrine.png',
  '黄塔晶': '/assets/crystals/10-黄塔晶 Yellow Tower Quartz.png',
  '星光粉水晶': '/assets/crystals/11-星光粉水晶 Starlight Rose Quartz.png',
  '果冻粉水晶': '/assets/crystals/12-果冻粉水晶 Jelly Rose Quartz.png',
  '海蓝宝冰': '/assets/crystals/13-海蓝宝（冰）Aquamarine (Ice Type).png',
  '海蓝宝润': '/assets/crystals/14-海蓝宝（润）Aquamarine (Moist Type).png',
  '蓝晶石': '/assets/crystals/15-蓝晶石 Kyanite.png',
  '葡萄石': '/assets/crystals/16-葡萄石 Prehnite.png',
  '绿萤石': '/assets/crystals/17-绿萤石 Green Fluorite.png',
  '绿幽灵': '/assets/crystals/18-绿幽灵 Green Phantom Quartz.png',
  '蓝月光': '/assets/crystals/19-蓝月光 Blue Moonstone.png',
  '灰月光': '/assets/crystals/20-灰月光 Grey Moonstone.png',
  '橙月光': '/assets/crystals/21-橙月光 Orange Moonstone.png',
  '草莓晶': '/assets/crystals/22-草莓晶 Strawberry Quartz.png',
  '鸽血红草莓晶': '/assets/crystals/23-鸽血红草莓晶 Pigeon Blood Strawberry Quartz.png',
  '红玛瑙': '/assets/crystals/24-红玛瑙 Red Agate.png',
  '绿龙晶': '/assets/crystals/25-绿龙晶 Seraphinite.png',
  '茶水晶': '/assets/crystals/26-茶水晶 Smoky Quartz.png',
  '金曜石': '/assets/crystals/27-金曜石 Gold Obsidian.png',
  '银曜石': '/assets/crystals/28-银曜石 Silver Obsidian.png',
  '黑曜石': '/assets/crystals/29-黑曜石 Black Obsidian.png',
  '金虎眼': '/assets/crystals/30-金虎眼 Gold Tiger\'s Eye.png',
  '黄虎眼': '/assets/crystals/31-黄虎眼 Yellow Tiger\'s Eye.png',
  '蓝虎眼': '/assets/crystals/32-蓝虎眼 Blue Tiger\'s Eye.png',
  '黑发晶': '/assets/crystals/33-黑发晶 Black Rutilated Quartz.png',
  '绿发晶': '/assets/crystals/34-绿发晶 Green Rutilated Quartz.png',
  '金发晶': '/assets/crystals/35-金发晶 Gold Rutilated Quartz.png',
  '黑金超七': '/assets/crystals/37-黑金超七 Black Gold Super Seven.png',
  '红胶花': '/assets/crystals/38-红胶花 Red Phantom Flower Quartz.png',
  '黄胶花': '/assets/crystals/39-38.黄胶花 Yellow Phantom Flower Quartz.png',
  '沉香': '/assets/crystals/40-沉香 Agarwood.png',
  '黄杨木莲花': '/assets/crystals/41-黄杨木莲花 Boxwood Lotus.png',
  '百香籽直切': '/assets/crystals/42-41.百香籽直切 Bodhi Seed.png',
  '树王小金刚': '/assets/crystals/43-树王小金刚 King Bodhi Nut.png',
  '檀木隔珠': '/assets/crystals/44-檀木隔珠 Sandalwood.png',
  '檀木': '/assets/crystals/47-檀木 Sandalwood.png',
  '绿檀': '/assets/crystals/48-绿檀 Green Sandalwood.png',
  '老山檀': '/assets/crystals/49-老山檀（浮水）Laoshan Sandalwood (Floating).png',
  '浅晴水和田玉': '/assets/crystals/50-浅晴水和田玉 Light Clear Water Jade.png',
  '碧玉': '/assets/crystals/51-碧玉 Green Jasper.png',
  '青提岫玉': '/assets/crystals/52-青提岫玉 Green Grape Serpentine.png',
  '帝王砂': '/assets/crystals/53-帝王砂 Imperial Cinnabar.png',
  '紫金砂': '/assets/crystals/54-紫金砂 Purple Gold Cinnabar.png',
  '淡水珍珠': '/assets/crystals/55-淡水珍珠 Freshwater Pearl.png',
  '贝珠': '/assets/crystals/56-贝珠 Shell Pearl.png',
  '海纹石': '/assets/crystals/57-海纹石 Larimar.png',
  '蜜蜡': '/assets/crystals/58-蜜蜡 Beeswax Amber.png',
  '石榴石': '/assets/crystals/59-石榴石 Garnet.png',
  '青金石': '/assets/crystals/60-青金石 Lapis Lazuli.png',
  '白水晶冰糖': '/assets/crystals/61-白水晶冰糖 Rock Candy White Crystal.png',
  '白水晶冰块': '/assets/crystals/62-白水晶冰块 Ice Cube White Crystal.png',
  '南瓜珠': '/assets/crystals/63-南瓜珠 Pumpkin Carved Bead.png',
  '白水晶双尖': '/assets/crystals/64-白水晶双尖 White Crystal Double Point.png',
  '黄水晶双尖': '/assets/crystals/65-黄水晶双尖 Citrine Double Point.png',
  '粉水晶双尖': '/assets/crystals/66-粉水晶双尖 Rose Quartz Double Point.png',
  '黑金超七双尖': '/assets/crystals/67-黑金超七双尖 Black Gold Super Seven Double Point.png',
  '绿松石隔珠': '/assets/crystals/68-绿松石隔珠 Turquoise Spacer Bead.png',
  // 兼容原有名称
  '紫水晶': '/assets/crystals/4-巴西紫水晶 Brazilian Amethyst.png',
  '黄水晶': '/assets/crystals/8-冰糖雪梨黄水晶 Rock Candy Pear Citrine.png',
  '粉水晶': '/assets/crystals/11-星光粉水晶 Starlight Rose Quartz.png',
  '月光石': '/assets/crystals/19-蓝月光 Blue Moonstone.png',
  '虎眼石': '/assets/crystals/30-金虎眼 Gold Tiger\'s Eye.png',
  '沉香木': '/assets/crystals/40-沉香 Agarwood.png',
  '紫檀木': '/assets/crystals/47-檀木 Sandalwood.png',
  '金菩提子': '/assets/crystals/43-树王小金刚 King Bodhi Nut.png',
  // 配饰素材
  '星星隔珠': '/assets/accessories/1.星星隔珠.png',
  '时来运转': '/assets/accessories/2.时来运转.png',
  '华托金色': '/assets/accessories/3-华托金色.png',
  '华托金色←': '/assets/accessories/3-华托金色.png',
  '花托银色': '/assets/accessories/4-花托银色.png',
  '花托银色←': '/assets/accessories/4-花托银色.png',
  '双面莲': '/assets/accessories/5-双面莲.png',
  '图腾隔珠': '/assets/accessories/6-图腾隔珠.png',
  '醒狮': '/assets/accessories/7-.醒狮.png',
  '天珠': '/assets/accessories/8-天珠.png',
  '编织隔珠': '/assets/accessories/9-编织隔珠.png',
  '五瓣花': '/assets/accessories/10-五瓣花.png',
  '金色隔珠': '/assets/accessories/12-金色隔珠.png',
  '银色隔珠': '/assets/accessories/13-银色隔珠.png',
  '银色隔片': '/assets/accessories/14-银色隔片.png',
  '金色隔片': '/assets/accessories/15-金色隔片.png',
  '桂花': '/assets/accessories/16-桂花.png',
  // 兼容原有配饰名称
  '银隔珠': '/assets/accessories/13-银色隔珠.png',
  '银珠': '/assets/accessories/13-银色隔珠.png',
  '招财纳福': '/assets/accessories/2.时来运转.png',
  '小胖福': '/assets/accessories/3-华托金色.png',
  '钱币': '/assets/accessories/8-天珠.png',
  '花丝莲花': '/assets/accessories/5-双面莲.png',
  '桃花': '/assets/accessories/16-桂花.png',
};

// ==================== 数据定义 ====================

const BANNERS = [
  { id: 1, title: '✨ 新人专享礼', subtitle: '首次DIY立减30元', color: '#C9A96E' },
  { id: 2, title: '🌸 春季新品', subtitle: '天然水晶上新', color: '#A68B5B' },
];

const SHORTCUTS = [
  { id: 1, icon: '🎨', label: '自己DIY', page: 'diy' },
  { id: 2, icon: '💬', label: 'VIP交流群', page: 'home' },
  { id: 3, icon: '🎁', label: '新人福利', page: 'home' },
  { id: 4, icon: '📚', label: '水晶百科', page: 'home' },
];

const POPULAR_DESIGNS: Array<{id: number, name: string, beads: string, likes: number, price: number, author: string, beadNames: string[], tags: string[], useCount: number}> = [];

// ==================== 五行水晶数据 ====================
const WUXING_ELEMENTS: Record<string, { name: string; color: string; trait: string; crystals: string[] }> = {
  metal: { name: '金', color: '#C9A96E', trait: '决断·专注·贵气', crystals: ['白水晶', '白幽灵', '金发晶', '银钛晶', '月光石'] },
  wood: { name: '木', color: '#4CAF50', trait: '生机·成长·活力', crystals: ['绿幽灵', '葡萄石', '绿萤石', '东陵玉', '孔雀石'] },
  water: { name: '水', color: '#2196F3', trait: '智慧·流动·冷静', crystals: ['海蓝宝', '蓝晶石', '黑曜石', '灰月光', '蓝月光'] },
  fire: { name: '火', color: '#F44336', trait: '热情·魅力·温暖', crystals: ['红玛瑙', '红兔毛', '石榴石', '紫水晶', '红纹石'] },
  earth: { name: '土', color: '#795548', trait: '稳固·包容·滋养', crystals: ['黄水晶', '黄塔晶', '虎眼石', '茶晶', '钛晶'] },
};

const WUXING_SCENARIOS = [
  { id: 'career', name: '事业进阶', icon: '💼', description: '提升领导力与决策力，助事业腾飞', mainElement: 'metal', supportElement: 'fire', mainStones: ['金发晶', '白水晶', '虎眼石'], supportStones: ['红玛瑙', '石榴石'], designConcept: '金发晶为主石，增强自信与决断力；白水晶净化能量场；虎眼石提升行动力；红玛瑙与石榴石为辅，增添热情与动力。', targetUsers: ['职场人士', '创业者', '管理者'], shengRelation: '火生土，土生金' },
  { id: 'study', name: '学业考试', icon: '📚', description: '增强记忆力与专注力，考试顺利', mainElement: 'water', supportElement: 'metal', mainStones: ['海蓝宝', '紫水晶', '白水晶'], supportStones: ['月光石', '白幽灵'], designConcept: '海蓝宝开启智慧，提升表达能力；紫水晶增强记忆与专注；白水晶净化思绪；月光石与白幽灵平衡情绪，稳定发挥。', targetUsers: ['学生', '考研党', '考证人士'], shengRelation: '金生水' },
  { id: 'family', name: '家庭和睦', icon: '🏠', description: '增进亲情，营造温馨和谐氛围', mainElement: 'earth', supportElement: 'fire', mainStones: ['黄水晶', '粉水晶', '月光石'], supportStones: ['红玛瑙', '紫水晶'], designConcept: '黄水晶带来富足与稳定；粉水晶增进爱与包容；月光石柔和情绪；红玛瑙增添温暖活力，紫水晶促进沟通理解。', targetUsers: ['家庭主妇', '新婚夫妇', '亲子家庭'], shengRelation: '火生土' },
  { id: 'love', name: '情感桃花', icon: '💕', description: '招正缘、旺人缘，提升魅力', mainElement: 'fire', supportElement: 'wood', mainStones: ['粉水晶', '红纹石', '草莓晶'], supportStones: ['葡萄石', '绿幽灵'], designConcept: '粉水晶招正缘，提升人缘魅力；红纹石增强情感表达；草莓晶带来甜蜜能量；葡萄石与绿幽灵为辅，增添生机与成长。', targetUsers: ['单身人士', '情侣', '社交达人'], shengRelation: '木生火' },
  { id: 'wealth', name: '财富招财', icon: '💰', description: '聚财守财，提升财运与机遇', mainElement: 'earth', supportElement: 'metal', mainStones: ['黄水晶', '金发晶', '钛晶'], supportStones: ['白水晶', '白幽灵'], designConcept: '黄水晶主招财，金发晶提升财运能量；钛晶稳固财富根基；白水晶净化负能量；白幽灵增强守护，聚财不漏。', targetUsers: ['生意人', '投资者', '求财者'], shengRelation: '土生金' },
  { id: 'health', name: '健康平安', icon: '🍀', description: '调理身心，增强活力与免疫力', mainElement: 'wood', supportElement: 'water', mainStones: ['绿幽灵', '东陵玉', '葡萄石'], supportStones: ['海蓝宝', '黑曜石'], designConcept: '绿幽灵调理身心平衡；东陵玉增强免疫力与活力；葡萄石舒缓情绪；海蓝宝平衡身心；黑曜石排除负能量，守护健康。', targetUsers: ['亚健康人群', '养生爱好者', '运动人士'], shengRelation: '水生木' },
];

const BEAD_CATEGORIES = [
  { id: 'crystal', name: '水晶' },
  { id: 'gemstone', name: '宝石' },
  { id: 'wood', name: '木珠' },
  { id: 'accessory', name: '配饰' },
  { id: 'all', name: '全部' },
];

const BEAD_ITEMS = [
  // ===== 水晶 =====
  { id: 1, name: '白水晶', price: 8, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 2, name: '白幽灵', price: 18, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 3, name: '巴西紫水晶', price: 15, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 4, name: '玻利维亚紫水晶', price: 18, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 5, name: '薰衣草紫水晶', price: 22, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 6, name: '乌拉圭紫水晶', price: 28, category: 'crystal', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 7, name: '冰糖雪梨黄水晶', price: 18, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 8, name: '柠檬黄水晶', price: 16, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 9, name: '星光粉水晶', price: 20, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 10, name: '果冻粉水晶', price: 22, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 11, name: '紫锂辉', price: 35, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 12, name: '黄塔晶', price: 25, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 13, name: '草莓晶', price: 20, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 14, name: '鸽血红草莓晶', price: 38, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 15, name: '茶水晶', price: 12, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 16, name: '黑曜石', price: 10, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 17, name: '金曜石', price: 15, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 18, name: '银曜石', price: 15, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 19, name: '黑金超七', price: 45, category: 'crystal', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 20, name: '红胶花', price: 28, category: 'crystal', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 21, name: '黄胶花', price: 28, category: 'crystal', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 22, name: '白水晶冰糖', price: 12, category: 'crystal', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 23, name: '白水晶冰块', price: 12, category: 'crystal', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 24, name: '南瓜珠', price: 15, category: 'crystal', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 25, name: '白水晶双尖', price: 18, category: 'crystal', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 26, name: '黄水晶双尖', price: 22, category: 'crystal', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 27, name: '粉水晶双尖', price: 22, category: 'crystal', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 28, name: '黑金超七双尖', price: 55, category: 'crystal', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  // ===== 宝石 =====
  { id: 30, name: '海蓝宝冰', price: 45, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 31, name: '海蓝宝润', price: 55, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 32, name: '蓝晶石', price: 30, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 33, name: '葡萄石', price: 28, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 34, name: '绿萤石', price: 20, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 35, name: '绿幽灵', price: 25, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 36, name: '蓝月光', price: 32, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 37, name: '灰月光', price: 28, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 38, name: '橙月光', price: 30, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 39, name: '红玛瑙', price: 14, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 40, name: '绿龙晶', price: 38, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 41, name: '金虎眼', price: 20, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 42, name: '黄虎眼', price: 18, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 43, name: '蓝虎眼', price: 22, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 44, name: '黑发晶', price: 28, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 45, name: '绿发晶', price: 32, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 46, name: '金发晶', price: 22, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 47, name: '浅晴水和田玉', price: 68, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 48, name: '碧玉', price: 35, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 49, name: '青提岫玉', price: 30, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 50, name: '帝王砂', price: 45, category: 'gemstone', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 51, name: '紫金砂', price: 45, category: 'gemstone', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 52, name: '淡水珍珠', price: 25, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 53, name: '贝珠', price: 18, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 54, name: '海纹石', price: 55, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 55, name: '蜜蜡', price: 65, category: 'gemstone', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 56, name: '石榴石', price: 22, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 57, name: '青金石', price: 40, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 58, name: '绿松石隔珠', price: 15, category: 'gemstone', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  // ===== 木珠 =====
  { id: 60, name: '沉香', price: 35, category: 'wood', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 61, name: '黄杨木莲花', price: 20, category: 'wood', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 62, name: '百香籽直切', price: 15, category: 'wood', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 63, name: '树王小金刚', price: 25, category: 'wood', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 64, name: '檀木隔珠', price: 8, category: 'wood', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 65, name: '檀木', price: 12, category: 'wood', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 66, name: '绿檀', price: 15, category: 'wood', sizes: ['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'] },
  { id: 67, name: '老山檀', price: 45, category: 'wood', sizes: ['8mm', '10mm', '12mm', '14mm', '15mm'] },
  // ===== 配饰 =====
  { id: 70, name: '星星隔珠', price: 5, category: 'accessory', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 71, name: '时来运转', price: 12, category: 'accessory', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 72, name: '华托金色', price: 8, category: 'accessory', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 73, name: '花托银色', price: 8, category: 'accessory', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 74, name: '双面莲', price: 15, category: 'accessory', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 75, name: '图腾隔珠', price: 10, category: 'accessory', sizes: ['6mm', '8mm', '10mm', '12mm', '15mm'] },
  { id: 76, name: '醒狮', price: 18, category: 'accessory', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 77, name: '天珠', price: 20, category: 'accessory', sizes: ['10mm', '12mm', '14mm', '15mm'] },
  { id: 78, name: '编织隔珠', price: 6, category: 'accessory', sizes: ['6mm', '8mm', '10mm', '15mm'] },
  { id: 79, name: '五瓣花', price: 8, category: 'accessory', sizes: ['8mm', '10mm', '12mm', '15mm'] },
  { id: 80, name: '金色隔珠', price: 5, category: 'accessory', sizes: ['4mm', '6mm', '8mm', '15mm'] },
  { id: 81, name: '银色隔珠', price: 5, category: 'accessory', sizes: ['4mm', '6mm', '8mm', '15mm'] },
  { id: 82, name: '银色隔片', price: 4, category: 'accessory', sizes: ['4mm', '6mm', '15mm'] },
  { id: 83, name: '金色隔片', price: 4, category: 'accessory', sizes: ['4mm', '6mm', '15mm'] },
  { id: 84, name: '桂花', price: 6, category: 'accessory', sizes: ['8mm', '10mm', '12mm', '15mm'] },
];

const PROFILE_MENU = [
  { id: 1, icon: '🎨', label: '我的设计', count: 3 },
  { id: 2, icon: '📦', label: '我的订单', count: 1 },
  { id: 3, icon: '📍', label: '地址管理' },
  { id: 4, icon: '🎫', label: '优惠券', count: 2 },
  { id: 5, icon: '📋', label: '我的任务' },
  { id: 6, icon: '👥', label: '我的好友' },
  { id: 7, icon: '🔔', label: '消息通知', count: 5 },
  { id: 8, icon: '🤝', label: '邀请好友' },
  { id: 9, icon: '💬', label: '联系客服' },
];

// ==================== 手串预览组件（可拖动 + 点击删除 + 真实水晶质感）====================

// 判断是否为配饰（扁平金属件）
const isFlatAccessory = (name: string): boolean => {
  // 有真实图片素材时，所有珠子均以圆形/正方形显示，由图片本身呈现形状
  // 仅对没有素材图且是扁平配饰的珠子使用扁平样式
  if (CRYSTAL_IMAGES[name]) return false;
  return ['招财纳福', '小胖福', '钱币', '花丝莲花', '桃花'].includes(name);
};

// 判断是否为隔珠（配饰类都作为隔珠处理，支持点击放置到间隙）
const isSpacerBead = (name: string): boolean => {
  return BEAD_ITEMS.some(item => item.name === name && item.category === 'accessory');
};

// 获取花托类配饰的显示名称（统一方向，不再自动交替）
const getSpacerDisplayName = (name: string, _spacerIndex: number): string => {
  return name.replace(/←$/, '');
};

// 判断是否为金色隔珠
const isGoldSpacer = (name: string): boolean => {
  return ['金色隔珠', '金色隔片', '华托金色'].includes(name);
};

// 判断是否为银色隔珠
const isSilverSpacer = (name: string): boolean => {
  return ['银色隔珠', '银色隔片', '花托银色'].includes(name);
};

// 圆环布局常量（紧密排列，无空隙）
const CENTER_X = 145;
const CENTER_Y = 145;
const FIXED_RADIUS = 95;

// 根据珠子名称生成真实水晶渲染（优先使用真实素材图片，回退到 LinearGradient 渐变色）
function renderCrystalBead(
  name: string,
  beadSize: number,
  isDark: boolean,
  isFlat: boolean,
  isEditing: boolean,
  rotationAngle: number = 0, // 珠子旋转角度（度），使纹理线与圆周切线方向一致
  onPressIn?: () => void,
  onPressOut?: () => void,
  onPress?: () => void,
) {
  const color = CRYSTAL_COLORS[name] || '#888';
  const borderRadius = isFlat ? beadSize * 0.15 : beadSize / 2;
  const isGold = ['招财纳福', '小胖福', '钱币', '花丝莲花'].includes(name);
  const isSpacer = isSpacerBead(name);
  const isGoldSpacerBead = isGoldSpacer(name);
  const isSilverSpacerBead = isSilverSpacer(name);

  // 优先使用真实素材图片
  const imgUri = CRYSTAL_IMAGES[name];
  if (imgUri) {
    // 核心问题：View 容器即使透明，圆形边界也会推开相邻珠子
    // 解决方案：隔珠用透明无尺寸容器包裹，图片绝对定位渲染到自己完整视觉尺寸
    // 视觉上隔珠"骑"在珠子之间的间隙上，压住两侧珠子边缘
    const isSpacerItem = isSpacerBead(name);
    const isAccessoryItem = isFlatAccessory(name);
    // 隔珠视觉尺寸目标：相对于 beadSize 的目标显示比例
    // 花托类（蝴蝶型）= 珠子的 35%，小巧精致
    // 隔片类（扁平）= 珠子的 30%，薄片状
    // 隔珠类（圆形）= 珠子的 45%，适中
    const spacerTargets: Record<string, number> = {
      '花托银色': 0.35, '花托金色': 0.35, '华托金色': 0.35,
      '银色隔珠': 0.45, '银色隔片': 0.30,
      '金色隔珠': 0.45, '金色隔片': 0.30, '星星隔珠': 0.35, '编织隔珠': 0.35,
      '图腾隔珠': 0.40, '双面莲': 0.40, '五瓣花': 0.40,
      '绿松石隔珠': 0.35, '檀木隔珠': 0.35, '桂花': 0.35,
      '时来运转': 0.45, '醒狮': 0.40, '天珠': 0.40,
    };
    const targetRatio = spacerTargets[name] || 0.35;
    // 内容比例：花托银色29%，表示实际内容只占图片的29%
    const contentRatios: Record<string, number> = {
      '花托银色': 0.37, '花托金色': 0.37, '华托金色': 0.37,
      '银色隔珠': 0.85, '银色隔片': 0.33,
      '金色隔珠': 0.85, '金色隔片': 0.37, '星星隔珠': 0.50, '编织隔珠': 0.50,
      '图腾隔珠': 0.60, '双面莲': 0.50, '五瓣花': 0.50,
      '绿松石隔珠': 0.45, '檀木隔珠': 0.45, '桂花': 0.40,
      '时来运转': 0.55, '醒狮': 0.55, '天珠': 0.55,
    };
    const contentRatio = contentRatios[name] || 0.50;
    // 视觉尺寸 = 珠子尺寸 * 目标比例（蝴蝶内容最终会是这个尺寸）
    const visualSize = isSpacerItem ? beadSize * targetRatio : beadSize;
    // 图片需要放大 (1/contentRatio) 倍才能让蝴蝶内容填满 visualSize
    const imgScale = isSpacerItem ? (1.0 / contentRatio) : 1.0;
    const borderRadius = isFlat ? visualSize * 0.15 : visualSize / 2;
    // 反向花托上下翻转（← 表示反向/倒放）
    const isFlipped = name.endsWith('←');
    // 隔珠：填满父容器（保证触摸区域），内部居中显示小尺寸图片
    if (isSpacerItem) {
      return (
        <View
          pointerEvents="none"
          style={{
            width: beadSize,
            height: beadSize,
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: beadSize / 2,
          }}
        >
          <Image
            source={{ uri: encodeURI(imgUri) }}
            draggable={false}
            pointerEvents="none"
            style={{
              width: beadSize,
              height: beadSize,
              resizeMode: 'contain',
              transform: [
                ...(rotationAngle !== 0 ? [{ rotate: `${rotationAngle + (isFlipped ? 180 : 0)}deg` }] : []),
              ],
            }}
          />
        </View>
      );
    }
    // 珠子/配饰：正常渲染
    const imgHeight = isFlat ? beadSize * 0.9 : beadSize;
    return (
      <View
        style={{
          width: beadSize,
          height: imgHeight,
          borderRadius,
          overflow: 'hidden',
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center',
          transform: [{ rotate: `${rotationAngle + (isFlipped ? 180 : 0)}deg` }],
        }}
      >
        <Image
          source={{ uri: encodeURI(imgUri) }}
          draggable={false}
          pointerEvents="none"
          style={{
            width: beadSize,
            height: imgHeight,
            borderRadius,
            resizeMode: 'contain',
            transform: [{ scale: 1.05 }],
          }}
        />
        {/* 立体阴影效果（底部暗部，增强球体感） */}
        {!isFlat && (
          <View
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: imgHeight * 0.35,
              borderBottomLeftRadius: borderRadius,
              borderBottomRightRadius: borderRadius,
              backgroundColor: 'rgba(0,0,0,0.15)',
            }}
          />
        )}
        {/* 编辑模式：删除按钮 */}
        {isEditing && (
          <TouchableOpacity
            style={{
              position: 'absolute', top: -6, right: -6,
              width: 20, height: 20, borderRadius: 10,
              backgroundColor: 'rgba(255,59,48,0.95)',
              justifyContent: 'center', alignItems: 'center', zIndex: 10,
              shadowColor: '#ff3b30', shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.5, shadowRadius: 3, elevation: 4,
            }}
            onPress={onPress}
          >
            <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', lineHeight: 18 }}>×</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // 不同水晶的质感参数
  const crystalType = getCrystalType(name);

  // 配饰扁平样式
  if (isFlat) {
    return (
      <View
        style={{
          width: beadSize,
          height: beadSize * 0.7,
          borderRadius,
          overflow: 'hidden',
          position: 'relative',
          borderWidth: isGold ? 1.5 : 1,
          borderColor: isGold ? 'rgba(255,215,0,0.7)' : 'rgba(255,255,255,0.3)',
        }}
      >
        {/* LinearGradient金属渐变底色 */}
        <LinearGradient
          colors={[
            crystalType.gradientColors?.[0] || color,
            crystalType.gradientColors?.[1] || crystalType.coreColor,
            crystalType.gradientColors?.[2] || color,
          ]}
          start={{ x: 0.2, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={{ flex: 1 }}
        />
        {/* 拉丝纹理 */}
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: crystalType.coreColor, opacity: 0.3,
        }} />
        {/* 高光条 */}
        <LinearGradient
          colors={['rgba(255,255,255,0.7)', 'rgba(255,255,255,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{
            position: 'absolute', top: 2, left: 3,
            width: beadSize * 0.5, height: 3, borderRadius: 2,
          }}
        />
        {/* 文字标识 */}
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          justifyContent: 'center', alignItems: 'center',
        }}>
          <Text style={{
            fontSize: beadSize * 0.22,
            color: isGold ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.7)',
            fontWeight: 'bold',
            textShadowColor: 'rgba(0,0,0,0.5)',
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 2,
          }}>
            {name.length > 2 ? name.slice(0, 2) : name}
          </Text>
        </View>
        {/* 编辑模式：删除按钮 */}
        {isEditing && (
          <TouchableOpacity
            style={{
              position: 'absolute', top: -6, right: -6,
              width: 18, height: 18, borderRadius: 9,
              backgroundColor: 'rgba(255,59,48,0.9)',
              justifyContent: 'center', alignItems: 'center', zIndex: 10,
            }}
            onPress={onPress}
          >
            <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // 水晶珠子渲染（球形质感 + LinearGradient）
  return (
    <View
      style={{
        width: beadSize,
        height: beadSize,
        borderRadius,
        overflow: 'hidden',
        position: 'relative',
        borderWidth: 0.5,
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.3)',
      }}
    >
      {/* ====== 核心：LinearGradient 模拟水晶球形光学效果 ====== */}
      <LinearGradient
        colors={crystalType.gradientColors || [
          isDark ? 'rgba(60,60,60,0.9)' : 'rgba(255,255,255,0.4)',
          color,
          isDark ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.3)',
        ]}
        start={{ x: 0.3, y: 0.1 }}   // 光源在左上
        end={{ x: 0.7, y: 0.95 }}    // 暗部在右下
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          borderRadius,
        }}
      />

      {/* 水晶内部纹理层 - 模拟天然矿物内含物 */}
      <View style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        borderRadius,
        backgroundColor: crystalType.coreColor,
        opacity: crystalType.coreOpacity,
      }} />
      {/* 弧形主纹理 - 跟随圆周切线方向旋转 */}
      <View style={{
        position: 'absolute',
        top: beadSize * 0.15, left: beadSize * 0.05,
        width: beadSize * 0.45, height: beadSize * 0.65,
        borderRadius: beadSize * 0.25,
        backgroundColor: crystalType.stripeColor,
        opacity: crystalType.stripeOpacity,
        transform: [{ rotate: `${-20 + rotationAngle}deg` }],
      }} />
      {/* 交叉纹理 - 跟随圆周切线方向旋转 */}
      <View style={{
        position: 'absolute',
        top: beadSize * 0.1, right: beadSize * 0.05,
        width: beadSize * 0.35, height: beadSize * 0.5,
        borderRadius: beadSize * 0.2,
        backgroundColor: crystalType.stripeColor,
        opacity: crystalType.stripeOpacity * 0.5,
        transform: [{ rotate: `${15 + rotationAngle}deg` }],
      }} />

      {/* ====== 主高光 - 左上角强光（LinearGradient 柔化） ====== */}
      <LinearGradient
        colors={[
          isDark ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.65)',
          isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.15)',
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        style={{
          position: 'absolute',
          top: beadSize * 0.04,
          left: beadSize * 0.06,
          width: beadSize * 0.58,
          height: beadSize * 0.42,
          borderRadius: beadSize * 0.28,
        }}
      />

      {/* ====== 中心高光光斑（最亮焦点） ====== */}
      <LinearGradient
        colors={[
          'rgba(255,255,255,0.8)',
          'rgba(255,255,255,0.1)',
        ]}
        start={{ x: 0.3, y: 0.2 }}
        end={{ x: 0.7, y: 0.8 }}
        style={{
          position: 'absolute',
          top: beadSize * 0.08,
          left: beadSize * 0.12,
          width: beadSize * 0.18,
          height: beadSize * 0.14,
          borderRadius: beadSize * 0.09,
        }}
      />

      {/* ====== 右下折射光（背光面微弱反射） ====== */}
      <LinearGradient
        colors={[
          isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.2)',
          'rgba(255,255,255,0)',
        ]}
        start={{ x: 0.2, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={{
          position: 'absolute',
          bottom: beadSize * 0.08,
          right: beadSize * 0.08,
          width: beadSize * 0.3,
          height: beadSize * 0.24,
          borderRadius: beadSize * 0.15,
        }}
      />

      {/* ====== 底部阴影（增加球体立体感） ====== */}
      <LinearGradient
        colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.3)']}
        start={{ x: 0.5, y: 0.3 }}
        end={{ x: 0.5, y: 1 }}
        style={{
          position: 'absolute',
          bottom: 0, left: beadSize * 0.1,
          width: beadSize * 0.8, height: beadSize * 0.35,
          borderRadius: beadSize * 0.18,
        }}
      />

      {/* 金色配饰发光效果 */}
      {isGold && (
        <>
          <View style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            borderRadius, borderWidth: 1.5, borderColor: 'rgba(255,215,0,0.6)',
          }} />
          <LinearGradient
            colors={['rgba(255,215,0,0.3)', 'rgba(255,180,0,0.1)']}
            start={{ x: 0.3, y: 0.2 }}
            end={{ x: 0.7, y: 0.8 }}
            style={{
              position: 'absolute',
              top: beadSize * 0.2, left: beadSize * 0.2,
              width: beadSize * 0.4, height: beadSize * 0.3,
              borderRadius: beadSize * 0.2,
            }}
          />
        </>
      )}

      {/* 编辑模式：删除按钮 */}
      {isEditing && (
        <TouchableOpacity
          style={{
            position: 'absolute', top: -6, right: -6,
            width: 20, height: 20, borderRadius: 10,
            backgroundColor: 'rgba(255,59,48,0.95)',
            justifyContent: 'center', alignItems: 'center', zIndex: 10,
            shadowColor: '#ff3b30', shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.5, shadowRadius: 3, elevation: 4,
          }}
          onPress={onPress}
        >
          <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', lineHeight: 18 }}>×</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

// 水晶质感参数（模拟不同天然水晶的光学特性 + LinearGradient 渐变色）
function getCrystalType(name: string) {
  const typeMap: Record<string, {
    coreColor: string;
    coreOpacity: number;
    stripeColor: string;
    stripeOpacity: number;
    gradientColors: [string, string, string];
  }> = {
    '白水晶': { coreColor: 'rgba(200,210,255,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(180,200,255,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(220,230,255,0.6)', 'rgba(248,248,255,0.9)', 'rgba(200,210,240,0.7)'] },
    '紫水晶': { coreColor: 'rgba(120,80,200,0.25)', coreOpacity: 0.25, stripeColor: 'rgba(100,60,180,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(167,139,250,0.7)', 'rgba(124,58,237,0.9)', 'rgba(76,29,149,0.8)'] },
    '黄水晶': { coreColor: 'rgba(255,200,100,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(200,150,50,0.15)', stripeOpacity: 0.15,
      gradientColors: ['rgba(253,224,150,0.7)', 'rgba(217,119,6,0.9)', 'rgba(154,52,18,0.7)'] },
    '粉水晶': { coreColor: 'rgba(255,180,200,0.25)', coreOpacity: 0.25, stripeColor: 'rgba(255,150,180,0.15)', stripeOpacity: 0.15,
      gradientColors: ['rgba(252,220,230,0.7)', 'rgba(249,168,212,0.9)', 'rgba(219,112,180,0.7)'] },
    '黑曜石': { coreColor: 'rgba(50,50,50,0.4)', coreOpacity: 0.4, stripeColor: 'rgba(80,80,80,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(55,55,55,0.95)', 'rgba(17,24,39,0.95)', 'rgba(0,0,0,0.95)'] },
    '绿幽灵': { coreColor: 'rgba(0,100,80,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(0,80,60,0.25)', stripeOpacity: 0.25,
      gradientColors: ['rgba(52,211,180,0.6)', 'rgba(5,150,105,0.9)', 'rgba(4,80,60,0.8)'] },
    '金发晶': { coreColor: 'rgba(180,120,50,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(200,160,80,0.3)', stripeOpacity: 0.3,
      gradientColors: ['rgba(251,191,100,0.7)', 'rgba(180,83,9,0.9)', 'rgba(100,40,0,0.8)'] },
    '月光石': { coreColor: 'rgba(220,220,240,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(200,200,230,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(240,240,250,0.7)', 'rgba(232,224,208,0.9)', 'rgba(200,195,220,0.7)'] },
    '虎眼石': { coreColor: 'rgba(140,100,50,0.25)', coreOpacity: 0.25, stripeColor: 'rgba(160,120,60,0.3)', stripeOpacity: 0.3,
      gradientColors: ['rgba(200,160,80,0.7)', 'rgba(146,64,14,0.9)', 'rgba(80,30,5,0.8)'] },
    '红玛瑙': { coreColor: 'rgba(150,30,30,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(120,20,20,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(248,150,140,0.6)', 'rgba(185,28,28,0.9)', 'rgba(100,10,10,0.8)'] },
    '紫檀木': { coreColor: 'rgba(60,20,30,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(80,30,40,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(80,30,40,0.7)', 'rgba(59,7,24,0.95)', 'rgba(30,3,12,0.9)'] },
    '沉香木': { coreColor: 'rgba(60,40,20,0.35)', coreOpacity: 0.35, stripeColor: 'rgba(80,60,30,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(90,60,30,0.7)', 'rgba(66,32,6,0.95)', 'rgba(30,15,3,0.9)'] },
    '银隔珠': { coreColor: 'rgba(200,200,210,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(180,180,190,0.15)', stripeOpacity: 0.15,
      gradientColors: ['rgba(230,230,240,0.7)', 'rgba(192,192,192,0.9)', 'rgba(140,140,155,0.7)'] },
    '金菩提子': { coreColor: 'rgba(140,100,40,0.25)', coreOpacity: 0.25, stripeColor: 'rgba(160,120,50,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(200,160,80,0.6)', 'rgba(133,77,14,0.9)', 'rgba(70,35,5,0.8)'] },
    '招财纳福': { coreColor: 'rgba(255,215,0,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(255,200,0,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(255,240,150,0.7)', 'rgba(255,215,0,0.9)', 'rgba(200,160,0,0.7)'] },
    '小胖福': { coreColor: 'rgba(218,165,32,0.25)', coreOpacity: 0.25, stripeColor: 'rgba(200,150,30,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(240,210,120,0.7)', 'rgba(218,165,32,0.9)', 'rgba(170,120,20,0.7)'] },
    '钱币': { coreColor: 'rgba(255,215,0,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(255,200,0,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(255,240,150,0.7)', 'rgba(255,215,0,0.9)', 'rgba(200,160,0,0.7)'] },
    '花丝莲花': { coreColor: 'rgba(255,215,0,0.3)', coreOpacity: 0.3, stripeColor: 'rgba(255,200,0,0.2)', stripeOpacity: 0.2,
      gradientColors: ['rgba(255,240,150,0.7)', 'rgba(255,215,0,0.9)', 'rgba(200,160,0,0.7)'] },
    '桃花': { coreColor: 'rgba(255,105,180,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(255,80,160,0.15)', stripeOpacity: 0.15,
      gradientColors: ['rgba(255,180,210,0.7)', 'rgba(255,105,180,0.9)', 'rgba(220,60,140,0.7)'] },
    '银珠': { coreColor: 'rgba(200,200,210,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(180,180,190,0.15)', stripeOpacity: 0.15,
      gradientColors: ['rgba(240,240,245,0.7)', 'rgba(232,232,232,0.9)', 'rgba(180,180,190,0.7)'] },
  };
  return typeMap[name] || { coreColor: 'rgba(150,150,150,0.2)', coreOpacity: 0.2, stripeColor: 'rgba(100,100,100,0.15)', stripeOpacity: 0.15, gradientColors: ['rgba(200,200,200,0.7)', 'rgba(136,136,136,0.9)', 'rgba(80,80,80,0.7)'] };
}

// ==================== 设计广场用 - 只读水晶样式预览 ====================
function BraceletPreview({ beads, showLogo = true }: { beads: PreviewBead[]; showLogo?: boolean }) {
  const beadCount = beads.length;
  if (beadCount === 0) return null;

  // 将毫米尺寸转为像素尺寸
  const pixelBeads = beads.map(b => ({
    name: b.name,
    size: sizeToPixel[b.size] || 30,
    isSpacer: isSpacerBead(b.name),
  }));

  // 半径基于所有珠子总宽度计算（修正系数 0.92 消除视觉空隙）
  const allTotalWidth = pixelBeads.reduce((sum, b) => sum + b.size, 0);
  const calculatedRadius = allTotalWidth / (2 * Math.PI) * 0.92;
  const targetRadius = Math.max(50, Math.min(150, calculatedRadius));
  const avgBeadSize = beadCount > 0 ? allTotalWidth / beadCount : 30;

  // 所有珠子（包括隔珠）按实际尺寸紧密排列在圆环上
  // 角度 = 累计弧长 / 总弧长 × 360°，确保相邻珠子边缘相接无空隙
  const angles: Record<number, number> = {};
  let cumulativeArc = 0;
  pixelBeads.forEach((bead, idx) => {
    const arcPos = cumulativeArc + bead.size / 2;
    angles[idx] = (arcPos / allTotalWidth) * 2 * Math.PI - Math.PI / 2;
    cumulativeArc += bead.size;
  });

  return (
    <View style={styles.braceletContainer}>
      {/* 圆环绳子底座 */}
      <View style={[styles.braceletString, {
        width: targetRadius * 2 + avgBeadSize * 0.3,
        height: targetRadius * 2 + avgBeadSize * 0.3,
        borderRadius: targetRadius + avgBeadSize * 0.15,
        left: CENTER_X - targetRadius - avgBeadSize * 0.15,
        top: CENTER_Y - targetRadius - avgBeadSize * 0.15,
        zIndex: 1,
      }]} />

      {/* 所有珠子按实际尺寸紧密排列（无空隙） */}
      {(() => { let sIdx = 0; return pixelBeads.map((bead, index) => {
        const name = getSpacerDisplayName(bead.name, bead.isSpacer ? sIdx++ : sIdx);
        const beadSize = bead.size;
        const angle = angles[index];
        const isDark = ['黑曜石', '紫檀木', '沉香木'].includes(name);
        const isFlat = isFlatAccessory(name);

        const beadRadius = targetRadius;

        const x = CENTER_X + beadRadius * Math.cos(angle);
        const y = CENTER_Y + beadRadius * Math.sin(angle);

        // 黄杨木莲花竖着串（径向方向），其他配饰横着排列（切线方向）
        const isVerticalLotus = name === '黄杨木莲花';
        const rotationAngle = isVerticalLotus
          ? (angle * 180 / Math.PI)        // 径向方向，竖着
          : (angle * 180 / Math.PI) + 90;  // 切线方向，横着

        return (
          <View key={`${name}-${index}`} style={[styles.bead, {
            width: beadSize,
            height: beadSize,
            borderRadius: isFlat ? beadSize * 0.15 : beadSize / 2,
            left: x - beadSize / 2,
            top: y - beadSize / 2,
            zIndex: bead.isSpacer ? 10 : 5,
          }]}>
            {renderCrystalBead(name, beadSize, isDark, isFlat, false, rotationAngle)}
          </View>
        );
      }); })()}

      {/* 中间 Logo - 韩一珠品牌图片 */}
      {showLogo && (
        <Image
          source={require('./assets/logo.png')}
          style={{
            position: 'absolute',
            left: CENTER_X - avgBeadSize * 0.60,
            top: CENTER_Y - avgBeadSize * 0.60,
            width: avgBeadSize * 1.20,
            height: avgBeadSize * 1.20,
            borderRadius: avgBeadSize * 0.60,
            resizeMode: 'cover',
          }}
        />
      )}
    </View>
  );
}

// ==================== 音效管理（水晶碰撞声）====================
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let soundInstance: any = null;
let soundLoaded = false;

// 基于Web Audio API合成的水滴音效（拾起珠子时：清脆短促的"滴"）
async function playCrystalSound() {
  try {
    if (Platform.OS === 'web') {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const t = audioCtx.currentTime;

      // 水滴"滴" — 高频短促下滑
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(2200, t);
      osc.frequency.exponentialRampToValueAtTime(600, t + 0.06);
      gain.gain.setValueAtTime(0.12, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.1);

      // 轻微振动反馈（移动端）
      if (Platform.OS !== 'web') {
        Vibration.vibrate(10);
      }
    } else {
      if (!soundLoaded) {
        try { Vibration.vibrate(10); } catch(_) {}
      }
    }
  } catch (_) {
    // 静默失败 - 音效不影响功能
  }
}

// 放下珠子时的水滴余波声（轻柔确认音）
async function playDropSound() {
  try {
    if (Platform.OS === 'web') {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const t = audioCtx.currentTime;

      // 水滴落入后的"咚"余波
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(200, t + 0.15);
      gain.gain.setValueAtTime(0.15, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(t);
      osc.stop(t + 0.2);

      // 轻柔气泡感
      const osc2 = audioCtx.createOscillator();
      const gain2 = audioCtx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(500, t + 0.03);
      osc2.frequency.exponentialRampToValueAtTime(250, t + 0.18);
      gain2.gain.setValueAtTime(0.06, t + 0.03);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
      osc2.connect(gain2).connect(audioCtx.destination);
      osc2.start(t + 0.03);
      osc2.stop(t + 0.22);
    } else {
      try { Vibration.vibrate(20); } catch(_) {}
    }
  } catch (_) {}
}

// ==================== DIY设计用 - 自由拖动+删除水晶预览（纯RN PanResponder）====================
type PreviewBead = { name: string; size: string };

// 尺寸到像素的映射（全局共用）
const sizeToPixel: Record<string, number> = {
  '6mm': 18,
  '8mm': 24,
  '10mm': 30,
  '12mm': 36,
  '14mm': 42,
  '15mm': 45,
};

// 将设计数据转换为预览用的珠子数组（兼容旧数据）
function designToPreviewBeads(design: { beadNames: string[]; beadEntries?: Array<{name: string; size: string}> }): PreviewBead[] {
  return design.beadEntries || design.beadNames.map(name => ({ name, size: '10mm' }));
}

function EditableBraceletPreview({
  beads,
  onBeadsChange,
}: {
  beads: PreviewBead[];
  onBeadsChange: (newBeads: PreviewBead[]) => void;
}) {
  const beadCount = beads.length;
  const [deleteMode, setDeleteMode] = useState(false);
  const [dragInfo, setDragInfo] = useState<{ index: number; dx: number; dy: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDragging = useRef(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  // 珠子数量变化时重置状态
  const prevBeadCount = useRef(0);
  useEffect(() => {
    if (prevBeadCount.current !== beadCount) {
      prevBeadCount.current = beadCount;
      setDragInfo(null);
      setDeleteMode(false);
      isDragging.current = false;
      dragStartPos.current = null;
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    }
  }, [beadCount]);

  // 用 ref 保存最新值，避免闭包捕获旧 state
  const beadsRef = useRef(beads);
  beadsRef.current = beads;
  const deleteModeRef = useRef(deleteMode);
  deleteModeRef.current = deleteMode;
  const targetRadiusRef = useRef(95);
  const beadAnglesRef = useRef<Record<number, number>>({});
  const dragInfoRef = useRef<{ index: number; dx: number; dy: number } | null>(null);
  dragInfoRef.current = dragInfo;

  // 清理全局事件监听
  useEffect(() => {
    const cleanup = () => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    };
    // @ts-ignore
    if (typeof window !== 'undefined') {
      // @ts-ignore
      window.__braceletDragCleanup = cleanup;
    }
    return cleanup;
  }, []);

  // ========== 拖拽系统（Web 鼠标 + 触摸 + Native 兼容） ==========
  const dragActiveIndex = useRef<number | null>(null);

  const startDrag = useCallback((pageX: number, pageY: number, index: number) => {
    // 防止重复触发（pointerdown + touchstart/mousedown 可能同时触发）
    if (dragActiveIndex.current !== null) return;
    dragActiveIndex.current = index;

    dragStartPos.current = { x: pageX, y: pageY };
    isDragging.current = false;
    setDragInfo({ index, dx: 0, dy: 0 });
    playCrystalSound();

    // 长按检测（进入删除模式）
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      if (!isDragging.current) {
        setDeleteMode(prev => !prev);
        setDragInfo(null);
        dragStartPos.current = null;
        playDropSound();
        longPressTimer.current = null;
        dragActiveIndex.current = null;
      }
    }, 500);

    // ====== 全局 move/up 监听器 ======
    const onMove = (ev: any) => {
      ev.preventDefault?.();
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      const mx = ev.clientX ?? ev.pageX ?? (ev.touches?.[0]?.pageX) ?? 0;
      const my = ev.clientY ?? ev.pageY ?? (ev.touches?.[0]?.pageY) ?? 0;
      const dx = mx - (dragStartPos.current?.x ?? 0);
      const dy = my - (dragStartPos.current?.y ?? 0);
      isDragging.current = true;
      setDragInfo({ index, dx, dy });
    };

    const onUp = (ev: any) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      const endX = ev.clientX ?? ev.pageX ?? (ev.changedTouches?.[0]?.pageX) ?? 0;
      const endY = ev.clientY ?? ev.pageY ?? (ev.changedTouches?.[0]?.pageY) ?? 0;
      const dx = endX - (dragStartPos.current?.x ?? 0);
      const dy = endY - (dragStartPos.current?.y ?? 0);
      const dist = Math.sqrt(dx * dx + dy * dy);

      // 清理所有全局监听
      const doc = typeof document !== 'undefined' ? document : null;
      if (doc) {
        doc.removeEventListener('pointermove', onMove);
        doc.removeEventListener('pointerup', onUp);
        doc.removeEventListener('mousemove', onMove);
        doc.removeEventListener('mouseup', onUp);
        doc.removeEventListener('touchmove', onMove);
        doc.removeEventListener('touchend', onUp);
      }
      dragActiveIndex.current = null;

      // 没拖动 → 当作点击
      if (!isDragging.current || dist < 5) {
        if (deleteModeRef.current) {
          const currentBeads = beadsRef.current;
          const newBeads = currentBeads.filter((_, i) => i !== index);
          onBeadsChange(newBeads);
          if (newBeads.length === 0) setDeleteMode(false);
          playDropSound();
        }
        setDragInfo(null);
        isDragging.current = false;
        dragStartPos.current = null;
        return;
      }

      // 拖动释放 → 计算落点角度，重排
      const currentBeads = beadsRef.current;
      const count = currentBeads.length;
      if (count === 0) { setDragInfo(null); isDragging.current = false; dragStartPos.current = null; return; }

      const step = (2 * Math.PI) / count;
      const angles = beadAnglesRef.current;

      // 用实际渲染角度计算原始位置
      const actualAngle = angles[index] !== undefined
        ? angles[index]
        : (index * step - Math.PI / 2);
      const origX = CENTER_X + targetRadiusRef.current * Math.cos(actualAngle);
      const origY = CENTER_Y + targetRadiusRef.current * Math.sin(actualAngle);
      const dropX = origX + dx;
      const dropY = origY + dy;

      // 计算落点相对于圆心的角度
      const ddx = dropX - CENTER_X;
      const ddy = dropY - CENTER_Y;
      let dropAngle = Math.atan2(ddy, ddx) + Math.PI / 2;
      if (dropAngle < 0) dropAngle += 2 * Math.PI;

      // 找到最近的珠子作为目标（基于实际渲染角度）
      let targetIndex = index;
      let minDiff = Infinity;
      for (let i = 0; i < count; i++) {
        if (i === index) continue;
        const beadAngle = angles[i] !== undefined
          ? angles[i]
          : (i * step - Math.PI / 2);
        let normalizedAngle = beadAngle + Math.PI / 2;
        while (normalizedAngle < 0) normalizedAngle += 2 * Math.PI;
        while (normalizedAngle >= 2 * Math.PI) normalizedAngle -= 2 * Math.PI;

        let diff = Math.abs(dropAngle - normalizedAngle);
        if (diff > Math.PI) diff = 2 * Math.PI - diff;
        if (diff < minDiff) {
          minDiff = diff;
          targetIndex = i;
        }
      }

      if (targetIndex !== index) {
        const newBeads = [...currentBeads];
        const [movedBead] = newBeads.splice(index, 1);
        const insertAt = targetIndex > index ? targetIndex - 1 : targetIndex;
        newBeads.splice(insertAt, 0, movedBead);
        onBeadsChange(newBeads);
        playDropSound();
      }

      setDragInfo(null);
      isDragging.current = false;
      dragStartPos.current = null;
    };

    // 注册所有可能的事件类型，确保 Web 鼠标+触摸全覆盖
    const doc = typeof document !== 'undefined' ? document : null;
    if (doc) {
      doc.addEventListener('pointermove', onMove);
      doc.addEventListener('pointerup', onUp);
      doc.addEventListener('mousemove', onMove);
      doc.addEventListener('mouseup', onUp);
      doc.addEventListener('touchmove', onMove, { passive: false });
      doc.addEventListener('touchend', onUp);
    }
  }, [onBeadsChange]);

  // 删除珠子（编辑模式下的红色X按钮）
  const handleDelete = useCallback((index: number) => {
    const currentBeads = beadsRef.current;
    const newBeads = currentBeads.filter((_, i) => i !== index);
    onBeadsChange(newBeads);
    if (newBeads.length === 0) setDeleteMode(false);
    playDropSound();
  }, [onBeadsChange]);

  if (beadCount === 0) return null;

  // 将毫米尺寸转为像素尺寸
  const pixelBeads = beads.map(b => ({
    name: b.name,
    size: sizeToPixel[b.size] || 30,
    isSpacer: isSpacerBead(b.name),
  }));

  // 分离普通珠子和配饰
  const regularBeads = pixelBeads.filter(b => !b.isSpacer);
  const spacerBeads = pixelBeads.filter(b => b.isSpacer);

  // 半径基于所有珠子总宽度计算（修正系数 0.92 消除视觉空隙）
  const allTotalWidth = pixelBeads.reduce((sum, b) => sum + b.size, 0);
  const calculatedRadius = allTotalWidth / (2 * Math.PI) * 0.92;
  const targetRadius = Math.max(50, Math.min(150, calculatedRadius));
  targetRadiusRef.current = targetRadius;

  // Logo 尺寸取平均珠子大小
  const avgBeadSize = beadCount > 0 ? allTotalWidth / beadCount : 30;

  // 所有珠子（包括隔珠）按实际尺寸紧密排列在圆环上
  // 角度 = 累计弧长 / 总弧长 × 360°，确保相邻珠子边缘相接无空隙
  const angles: Record<number, number> = {};
  let cumulativeArc = 0;
  pixelBeads.forEach((bead, idx) => {
    const arcPos = cumulativeArc + bead.size / 2;
    angles[idx] = (arcPos / allTotalWidth) * 2 * Math.PI - Math.PI / 2;
    cumulativeArc += bead.size;
  });

  // 更新实际渲染角度 ref，供拖拽释放时准确计算落点
  beadAnglesRef.current = angles;

  return (
    <View>
      <View style={styles.braceletContainer}>
        {/* 圆环绳子底座 */}
        <View style={[styles.braceletString, {
          width: targetRadius * 2 + avgBeadSize * 0.3,
          height: targetRadius * 2 + avgBeadSize * 0.3,
          borderRadius: targetRadius + avgBeadSize * 0.15,
          left: CENTER_X - targetRadius - avgBeadSize * 0.15,
          top: CENTER_Y - targetRadius - avgBeadSize * 0.15,
          zIndex: 1,
        }]} />

        {/* 所有珠子按实际尺寸紧密排列（无空隙） */}
        {(() => { let spacerIdx = 0; return pixelBeads.map((bead, originalIndex) => {
          const name = getSpacerDisplayName(bead.name, bead.isSpacer ? spacerIdx++ : spacerIdx);
          const beadSize = bead.size;
          const angle = angles[originalIndex];
          const isDark = ['黑曜石', '紫檀木', '沉香木'].includes(name);
          const isFlat = isFlatAccessory(name);
          const isDraggingBead = dragInfo !== null && dragInfo.index === originalIndex;

          // 珠子中心在圆环线上（targetRadius 已整体缩小）
          const beadRadius = targetRadius;

          const x = CENTER_X + beadRadius * Math.cos(angle);
          const y = CENTER_Y + beadRadius * Math.sin(angle);

          const shadowOffsetX = Math.cos(angle + Math.PI / 4) * 2.5;
          const shadowOffsetY = Math.sin(angle + Math.PI / 4) * 2.5;
          // 黄杨木莲花竖着串（径向方向），其他配饰横着排列（切线方向）
          const isVerticalLotus = name === '黄杨木莲花';
          const rotationAngle = isVerticalLotus
            ? (angle * 180 / Math.PI)        // 径向方向，竖着
            : (angle * 180 / Math.PI) + 90;  // 切线方向，横着

          const beadStyle: any = {
            position: 'absolute' as const,
            width: beadSize,
            height: beadSize,
            zIndex: isDraggingBead ? 100 : (bead.isSpacer ? 10 : 5),
            borderRadius: isFlat ? beadSize * 0.15 : beadSize / 2,
            // Web 端：禁止浏览器默认触摸行为，确保自定义拖拽接管
            touchAction: 'none',
            userSelect: 'none',
            cursor: isDraggingBead ? 'grabbing' : 'grab',
            WebkitUserSelect: 'none' as any,
          };

          if (isDraggingBead) {
            beadStyle.left = x - beadSize / 2 + dragInfo.dx;
            beadStyle.top = y - beadSize / 2 + dragInfo.dy;
            beadStyle.transform = [{ scale: 1.25 }];
            beadStyle.shadowColor = '#FFD700';
            beadStyle.shadowOffset = { width: 0, height: 8 };
            beadStyle.shadowOpacity = 0.8;
            beadStyle.shadowRadius = 16;
            beadStyle.elevation = 20;
          } else {
            beadStyle.left = x - beadSize / 2;
            beadStyle.top = y - beadSize / 2;
          }

          return (
            <View
              key={`editable-${name}-${originalIndex}`}
              style={beadStyle}
              // 鼠标按下
              onMouseDown={(e: any) => {
                e.preventDefault?.();
                e.stopPropagation?.();
                startDrag(e.clientX ?? e.pageX ?? 0, e.clientY ?? e.pageY ?? 0, originalIndex);
              }}
              // 触摸开始
              onTouchStart={(e: any) => {
                e.preventDefault?.();
                e.stopPropagation?.();
                const touch = e.nativeEvent?.touches?.[0];
                if (touch) {
                  startDrag(touch.pageX, touch.pageY, originalIndex);
                }
              }}
              // Pointer 事件兜底
              onPointerDown={(e: any) => {
                // 如果 mousedown/touchstart 已经触发了 startDrag，则跳过
                if (dragActiveIndex.current !== null) return;
                e.preventDefault?.();
                e.stopPropagation?.();
                const ne = e.nativeEvent;
                startDrag(ne?.pageX ?? ne?.clientX ?? 0, ne?.pageY ?? ne?.clientY ?? 0, originalIndex);
              }}
              // 防止 web 上右键菜单、图片拖拽等默认行为
              onContextMenu={(e: any) => e.preventDefault?.()}
            >
              {isDraggingBead && (
                <View style={{
                  position: 'absolute',
                  top: -4, left: -4, right: -4, bottom: -4,
                  borderRadius: isFlat ? beadSize * 0.15 + 4 : beadSize / 2 + 4,
                  borderWidth: 2,
                  borderColor: 'rgba(255,215,0,0.7)',
                  backgroundColor: 'rgba(255,215,0,0.1)',
                }} />
              )}
              {renderCrystalBead(
                name, beadSize, isDark, isFlat, deleteMode, rotationAngle,
                undefined, undefined,
                () => originalIndex >= 0 && handleDelete(originalIndex)
              )}
            </View>
          );
        }); })()}

        {/* 中间 Logo - 韩一珠品牌图片 */}
        <Image
          source={require('./assets/logo.png')}
          style={{
            position: 'absolute',
            left: CENTER_X - avgBeadSize * 0.60,
            top: CENTER_Y - avgBeadSize * 0.60,
            width: avgBeadSize * 1.20,
            height: avgBeadSize * 1.20,
            borderRadius: avgBeadSize * 0.60,
            resizeMode: 'cover',
          }}
        />
      </View>

      {/* 操作提示 */}
      {dragInfo !== null && (
        <View style={{
          marginTop: 8,
          paddingHorizontal: 16,
          paddingVertical: 6,
          backgroundColor: 'rgba(255,215,0,0.2)',
          borderRadius: 16,
          borderWidth: 1,
          borderColor: 'rgba(255,215,0,0.5)',
        }}>
          <Text style={{ color: '#FFD700', fontSize: 12, fontWeight: '500' }}>
            拖动中... 移动到目标位置松手即可
          </Text>
        </View>
      )}
      {deleteMode && beadCount > 0 && (
        <TouchableOpacity
          style={{
            marginTop: 8,
            paddingHorizontal: 16,
            paddingVertical: 6,
            backgroundColor: 'rgba(255,59,48,0.2)',
            borderRadius: 16,
            borderWidth: 1,
            borderColor: 'rgba(255,59,48,0.5)',
          }}
          onPress={() => setDeleteMode(false)}
        >
          <Text style={{ color: '#ff3b30', fontSize: 12, fontWeight: '500' }}>
            删除模式已开启 - 点击珠子删除 | 点击此处退出
          </Text>
        </TouchableOpacity>
      )}
      {dragInfo === null && !deleteMode && beadCount > 0 && (
        <View style={{
          marginTop: 8,
          paddingHorizontal: 16,
          paddingVertical: 6,
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 16,
        }}>
          <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: '300' }}>
            拖动珠子调整顺序 | 长按进入删除模式
          </Text>
        </View>
      )}
    </View>
  );
}

// ==================== 珠子选择组件（真实图片版）====================

function CrystalBeadItem({ item, onPress }: { item: typeof BEAD_ITEMS[0]; onPress: () => void }) {
  const color = CRYSTAL_COLORS[item.name] || '#888';
  const isDark = ['黑曜石', '紫檀木', '沉香木', '沉香'].includes(item.name);
  const isAccessory = isFlatAccessory(item.name) || item.category === 'accessory';
  const isGold = ['招财纳福', '小胖福', '钱币', '花丝莲花'].includes(item.name);
  const imgUri = CRYSTAL_IMAGES[item.name];

  return (
    <TouchableOpacity style={styles.beadItem} onPress={onPress}>
      <View style={[
        styles.beadItemCircle,
        { backgroundColor: imgUri ? 'transparent' : color },
        !imgUri && isAccessory && styles.beadItemAccessory,
        !imgUri && isGold && styles.beadItemGold,
      ]}>
        {imgUri ? (
          /* 有真实图片：直接展示素材 */
          <Image
            source={{ uri: encodeURI(imgUri) }}
            style={{
              width: '100%',
              height: '100%',
              transform: item.name.endsWith('←') ? [{ rotate: '180deg' }] : undefined,
            }}
            resizeMode="contain"
          />
        ) : isAccessory ? (
          <>
            <View style={styles.beadItemAccessoryHighlight} />
            <View style={styles.beadItemAccessoryBadge}>
              <Text style={styles.beadItemAccessoryBadgeText}>配</Text>
            </View>
          </>
        ) : (
          <>
            <View style={[styles.beadItemDrillHole, {
              backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            }]} />
            <View style={[styles.beadItemMainHighlight, {
              backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.5)',
            }]} />
            <View style={[styles.beadItemSecondaryHighlight, {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.25)',
            }]} />
            <View style={styles.beadItemBottomShadow} />
          </>
        )}
        {/* 金色描边（金色配饰，无图片时） */}
        {!imgUri && (isGold || isAccessory) && <View style={styles.beadItemGoldRing} />}
        {/* 普通描边（无图片时） */}
        {!imgUri && !isGold && !isAccessory && <View style={styles.beadItemRing} />}
      </View>
      <Text style={[styles.beadItemName, isAccessory && styles.beadItemAccessoryName]}>{item.name}</Text>
      {isAccessory ? (
        <Text style={styles.beadItemSizePrice}>{item.sizes[0]} ¥{item.price}</Text>
      ) : (
        <Text style={styles.beadItemPrice}>¥{item.price}</Text>
      )}
    </TouchableOpacity>
  );
}

// ==================== 首页 ====================

function HomeScreen({
  onNavigate,
  myDesigns,
  onDeleteDesign,
  onShareDesign,
}: {
  onNavigate: (page: string) => void;
  myDesigns: Array<{ id: number; name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number; createdAt: string }>;
  onDeleteDesign: (id: number) => void;
  onShareDesign?: (design: { name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number }) => void;
}) {
  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* 顶部 */}
        <View style={styles.header}>
          <Text style={styles.appTitle}>韩一珠</Text>
          <Text style={styles.appSubtitle}>你的专属手串设计</Text>
        </View>

        {/* Banner */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.bannerScroll}>
          {BANNERS.map(banner => (
            <View key={banner.id} style={[styles.banner, { backgroundColor: banner.color }]}>
              <Text style={styles.bannerTitle}>{banner.title}</Text>
              <Text style={styles.bannerSubtitle}>{banner.subtitle}</Text>
            </View>
          ))}
        </ScrollView>

        {/* 公告 */}
        <View style={styles.announcement}>
          <Text style={styles.announcementIcon}>📢</Text>
          <Text style={styles.announcementText}>新人DIY手串立减30元！限时活动火热进行中~</Text>
        </View>

        {/* 快捷入口 */}
        <View style={styles.shortcuts}>
          {SHORTCUTS.map(item => (
            <TouchableOpacity
              key={item.id}
              style={styles.shortcut}
              onPress={() => onNavigate(item.page)}
            >
              <View style={styles.shortcutIcon}>
                {item.id === 1 ? (
                  <View style={{ width: 32, height: 32, justifyContent: 'center', alignItems: 'center' }}>
                    <LinearGradient
                      colors={['#D4AF37', '#C9A96E', '#B8941F']}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                      style={{ width: 28, height: 28, borderRadius: 14 }}
                    />
                    <View style={{ position: 'absolute', flexDirection: 'row', flexWrap: 'wrap', width: 18, height: 18, justifyContent: 'center', alignItems: 'center' }}>
                      <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: '#8B7355', margin: 1 }} />
                      <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: '#A68B5B', margin: 1 }} />
                      <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: '#C9A96E', margin: 1 }} />
                      <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: '#D4AF37', margin: 1 }} />
                    </View>
                  </View>
                ) : item.id === 2 ? (
                  <View style={{ width: 28, height: 28, justifyContent: 'center', alignItems: 'center' }}>
                    <View style={{ width: 24, height: 20, borderRadius: 10, backgroundColor: '#4CAF50', justifyContent: 'center', alignItems: 'center' }}>
                      <View style={{ width: 12, height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.3)' }} />
                    </View>
                    <View style={{ width: 0, height: 0, backgroundColor: 'transparent', borderStyle: 'solid', borderLeftWidth: 5, borderRightWidth: 5, borderTopWidth: 6, borderLeftColor: 'transparent', borderRightColor: 'transparent', borderTopColor: '#4CAF50', marginTop: -2 }} />
                  </View>
                ) : (
                  <Text style={{ fontSize: 28, color: '#C9A96E' }}>{item.icon}</Text>
                )}
              </View>
              <Text style={styles.shortcutLabel}>{item.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* 五行水晶入口 */}
        <TouchableOpacity
          style={{ marginHorizontal: 20, marginBottom: 20, backgroundColor: '#1A1A1A', borderRadius: 14, padding: 16, borderWidth: 0.5, borderColor: 'rgba(201,169,110,0.15)' }}
          onPress={() => onNavigate('wuxing')}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <Text style={{ fontSize: 20, marginRight: 10 }}>☯️</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 15, fontWeight: '500', color: '#F5F0E8' }}>五行水晶设计</Text>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>根据五行能量选择适合你的水晶搭配</Text>
            </View>
            <Text style={{ fontSize: 18, color: '#C9A96E' }}>›</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {['金', '木', '水', '火', '土'].map((el, i) => {
              const colors = ['#C9A96E', '#4CAF50', '#2196F3', '#F44336', '#795548'];
              return (
                <View key={el} style={{ flex: 1, alignItems: 'center', paddingVertical: 6, backgroundColor: colors[i] + '15', borderRadius: 8 }}>
                  <Text style={{ fontSize: 14, color: colors[i], fontWeight: '500' }}>{el}</Text>
                </View>
              );
            })}
          </View>
        </TouchableOpacity>

        {/* 优惠券 */}
        <TouchableOpacity style={styles.coupon}>
          <View style={styles.couponLeft}>
            <Text style={styles.couponAmount}>¥30</Text>
            <Text style={styles.couponCondition}>满199可用</Text>
          </View>
          <View style={styles.couponDivider} />
          <View style={styles.couponRight}>
            <Text style={styles.couponTitle}>新人专属优惠券</Text>
            <Text style={styles.couponDate}>有效期至2026.06.30</Text>
          </View>
          <Text style={styles.couponGet}>立即领取</Text>
        </TouchableOpacity>

        {/* 设计广场 - 用户设计展示 */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>设计广场</Text>
            <TouchableOpacity onPress={() => onNavigate('designsquare')}>
              <Text style={styles.sectionMore}>查看更多 ›</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.designSquareGrid}>
            {POPULAR_DESIGNS.length === 0 && (
              <View style={{ width: '100%', alignItems: 'center', justifyContent: 'center', paddingVertical: 48 }}>
                <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.25)' }}>暂无设计，快去 DIY 你的手串吧</Text>
              </View>
            )}
            {POPULAR_DESIGNS.map(design => (
              <TouchableOpacity key={design.id} style={styles.designSquareCard} onPress={() => onNavigate('diy')}>
                {/* 手串预览 */}
                <View style={styles.designSquarePreview}>
                  <View style={{ transform: [{ scale: 0.72 }], marginHorizontal: -41, marginVertical: -41 }}>
                    <BraceletPreview beads={designToPreviewBeads(design)} showLogo={true} />
                  </View>
                </View>
                {/* 信息区 */}
                <View style={styles.designSquareInfo}>
                  <Text style={styles.designSquareName}>{design.name}</Text>
                  <View style={styles.designSquareTags}>
                    {design.tags.slice(0, 3).map((tag, idx) => (
                      <View key={idx} style={styles.designSquareTag}>
                        <Text style={styles.designSquareTagText}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                  <View style={styles.designSquareMeta}>
                    <Text style={styles.designSquareAuthor}>@{design.author}</Text>
                    <Text style={styles.designSquareUseCount}>{design.useCount}次使用</Text>
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* 我的设计 - 展示用户真实成品 */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>我的设计</Text>
            <TouchableOpacity onPress={() => onNavigate('designs')}>
              <Text style={styles.sectionMore}>查看全部 ›</Text>
            </TouchableOpacity>
          </View>

          {myDesigns.length === 0 ? (
            <View style={styles.emptyDesign}>
              <Text style={styles.emptyDesignIcon}>🎨</Text>
              <Text style={styles.emptyDesignTitle}>还没有设计作品</Text>
              <Text style={styles.emptyDesignDesc}>去DIY页面创建你的第一个手串设计吧</Text>
              <TouchableOpacity style={styles.emptyDesignBtn} onPress={() => onNavigate('diy')}>
                <Text style={styles.emptyDesignBtnText}>去DIY设计</Text>
              </TouchableOpacity>
            </View>
          ) : (
            myDesigns.map(design => (
              <View key={design.id} style={styles.designCard}>
                {/* 手串预览区 */}
                <View style={styles.designCardPreview}>
                  <View style={{ transform: [{ scale: 0.85 }], marginVertical: -22 }}>
                    <BraceletPreview beads={designToPreviewBeads(design)} showLogo={false} />
                  </View>
                </View>
                {/* 信息区 */}
                <View style={styles.designInfo}>
                  <Text style={styles.designName}>{design.name}</Text>
                  <View style={styles.designMeta}>
                    <Text style={styles.designBeads}>{design.beadNames.length} 颗珠子</Text>
                    <Text style={styles.designDate}>· {design.createdAt}</Text>
                  </View>
                <View style={styles.designBottom}>
                  <Text style={styles.designPrice}>¥{design.price}</Text>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {onShareDesign && (
                      <TouchableOpacity
                        style={[styles.designDeleteBtn, { borderColor: '#C9A96E', backgroundColor: 'rgba(201,169,110,0.1)' }]}
                        onPress={() => onShareDesign({ name: design.name, beadNames: design.beadNames, beadEntries: design.beadEntries, price: design.price })}
                      >
                        <Text style={[styles.designDeleteText, { color: '#C9A96E' }]}>分享</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      style={styles.designDeleteBtn}
                      onPress={() => onDeleteDesign(design.id)}
                    >
                      <Text style={styles.designDeleteText}>删除</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                </View>
              </View>
            ))
          )}
        </View>

        <View style={{ height: 20 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ==================== DIY设计页 ====================

function DIYScreen({ onSaveDesign }: {
  onSaveDesign: (design: { name: string; beadNames: string[]; beadEntries: Array<{name: string; size: string}>; price: number }) => void;
}) {
  const [selectedCategory, setSelectedCategory] = useState('crystal');
  const [bracelet, setBracelet] = useState<Array<{name: string; size: string}>>([]);
  const [wristSize, setWristSize] = useState(16);
  const [selectedSize, setSelectedSize] = useState('10mm');
  const [designName, setDesignName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showWristModal, setShowWristModal] = useState(false);

  // 尺寸到手串密度的映射（用于计算最大珠子数）
  const sizeToDensity: Record<string, number> = {
    '6mm': 2.5,
    '8mm': 2.0,
    '10mm': 1.8,
    '12mm': 1.5,
    '14mm': 1.3,
    '15mm': 1.2,
  };

  const beadSizeInMm = parseInt(selectedSize);
  const maxBeads = Math.floor(wristSize * (sizeToDensity[selectedSize] || 2.0));

  // 显示所有该分类的水晶，不再按尺寸过滤（每个水晶显示其所有可用尺寸）
  const filteredBeads = BEAD_ITEMS.filter(
    item => (selectedCategory === 'all' || item.category === selectedCategory)
  );

  const totalPrice = bracelet.reduce((sum, entry) => {
    const bead = BEAD_ITEMS.find(b => b.name === entry.name);
    return sum + (bead ? bead.price : 0);
  }, 0);

  const addBead = (bead: typeof BEAD_ITEMS[0]) => {
    // 配饰（隔珠）不占用普通珠子名额，允许额外添加
    const regularCount = bracelet.filter(b => !isSpacerBead(b.name)).length;
    const spacerCount = bracelet.filter(b => isSpacerBead(b.name)).length;
    const isAddingSpacer = isSpacerBead(bead.name);
    // 普通珠子上限由手围决定，配饰上限为普通珠子的2倍或最少10个
    const spacerLimit = Math.max(10, regularCount * 2);
    if (isAddingSpacer) {
      if (spacerCount < spacerLimit) {
        setBracelet([...bracelet, { name: bead.name, size: selectedSize }]);
      }
    } else {
      if (regularCount < maxBeads) {
        setBracelet([...bracelet, { name: bead.name, size: selectedSize }]);
      }
    }
  };

  const removeLast = () => {
    setBracelet(bracelet.slice(0, -1));
  };

  const clearBracelet = () => {
    setBracelet([]);
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.diyContainer}>
        {/* 预览区 */}
        <View style={styles.previewArea}>
          <EditableBraceletPreview 
            beads={bracelet}
            onBeadsChange={setBracelet}
          />
          <Text style={styles.previewHint}>
            {bracelet.length === 0 ? '↑ 选择珠子开始设计' : `已选 ${bracelet.filter(b => !isSpacerBead(b.name)).length}/${maxBeads} 颗`}
          </Text>
          <View style={styles.priceBar}>
            <Text style={styles.priceLabel}>总价：</Text>
            <Text style={styles.priceValue}>¥{totalPrice}</Text>
            <TouchableOpacity
              style={styles.wristCheckBtn}
              onPress={() => setShowWristModal(true)}
            >
              <Text style={styles.wristCheckBtnText}>查看手围</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* 操作栏 + 分类标签 合并一行 */}
        <View style={styles.actionBar}>
          <TouchableOpacity style={styles.actionBtn} onPress={removeLast}>
            <Text style={styles.actionBtnText}>↩</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionBtn} onPress={clearBracelet}>
            <Text style={styles.actionBtnText}>🗑</Text>
          </TouchableOpacity>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScrollInline}>
            {BEAD_CATEGORIES.map(cat => (
              <TouchableOpacity
                key={cat.id}
                style={[
                  styles.categoryTagInline,
                  selectedCategory === cat.id && styles.categoryTagActive,
                ]}
                onPress={() => setSelectedCategory(cat.id)}
              >
                <Text style={[
                  styles.categoryTextInline,
                  selectedCategory === cat.id && styles.categoryTextActive,
                ]}>
                  {cat.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* 规格选择 */}
        <View style={styles.sizeBar}>
          <Text style={styles.sizeBarLabel}>规格：</Text>
          {['6mm', '8mm', '10mm', '12mm', '14mm', '15mm'].map(size => (
            <TouchableOpacity
              key={size}
              style={[styles.sizeTag, selectedSize === size && styles.sizeTagActive]}
              onPress={() => setSelectedSize(size)}
            >
              <Text style={[styles.sizeText, selectedSize === size && styles.sizeTextActive]}>{size}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* 珠子列表 */}
        <FlatList
          data={filteredBeads}
          numColumns={4}
          keyExtractor={item => item.id.toString()}
          columnWrapperStyle={styles.beadRow}
          contentContainerStyle={styles.beadList}
          style={{ flex: 1, minHeight: 280 }}
          renderItem={({ item }) => (
            <CrystalBeadItem item={item} onPress={() => addBead(item)} />
          )}
        />

        {/* 底部按钮 */}
        <View style={styles.diyBottom}>
          <TouchableOpacity
            style={styles.saveBtn}
            onPress={() => {
              if (bracelet.length === 0) return;
              setShowSaveModal(true);
            }}
          >
            <Text style={styles.saveBtnText}>保存设计</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.orderBtn}>
            <Text style={styles.orderBtnText}>下单定制</Text>
          </TouchableOpacity>
        </View>

        {/* 手围尺寸表模态框 */}
        {showWristModal && (
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContent, { padding: 0, overflow: 'hidden' }]}>
              {/* 标题栏 */}
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                paddingVertical: 16,
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(255,255,255,0.08)',
                position: 'relative',
              }}>
                <Text style={{
                  fontSize: 18,
                  fontWeight: '400',
                  color: '#FFFFFF',
                  letterSpacing: 2,
                }}>手围对照表</Text>
                <TouchableOpacity
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: 12,
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={() => setShowWristModal(false)}
                >
                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, fontWeight: '300' }}>×</Text>
                </TouchableOpacity>
              </View>

              {/* 表格 */}
              <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator={false}>
                <View style={{ padding: 16 }}>
                  {/* 表头 */}
                  <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: 'rgba(201,169,110,0.3)', paddingBottom: 8, marginBottom: 8 }}>
                    <View style={{ width: 70, justifyContent: 'center', alignItems: 'center' }}>
                      <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: '300' }}>身高\体重</Text>
                    </View>
                    {['40','45','50','55','60','65','70','75','80','85','90','95','100'].map(w => (
                      <View key={w} style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: '300' }}>{w}</Text>
                      </View>
                    ))}
                  </View>
                  
                  {/* 表格数据 */}
                  {[
                    { height: '150-155', values: ['13.5','14','15','15.5','16','16','','','','','','',''] },
                    { height: '155-160', values: ['14','14','14','15','15.5','16','17','17.5','18','','','',''] },
                    { height: '160-165', values: ['','14','14.5','15','15.5','17','17.5','18','18','18.5','19','19','19'] },
                    { height: '165-170', values: ['','','15','15','15.5','16','16.5','17','17.5','18','19','19','19'] },
                    { height: '170-175', values: ['','','','15.5','16','16.5','17','17.5','18','19','19','19.5','19.5'] },
                    { height: '175-180', values: ['','','','16','17','17.5','17.5','18','18','18.5','18.5','19','19.5'] },
                    { height: '180-185', values: ['','','','','17','17.5','18','18','18.5','18.5','19','19.5','19.5'] },
                    { height: '185-190', values: ['','','','','','17','17.5','18','18.5','19','20','21',''] },
                  ].map((row, rowIndex) => (
                    <View key={row.height} style={{ 
                      flexDirection: 'row', 
                      paddingVertical: 6,
                      borderBottomWidth: 1,
                      borderBottomColor: 'rgba(255,255,255,0.04)',
                      backgroundColor: rowIndex % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                    }}>
                      <View style={{ width: 70, justifyContent: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: '300' }}>{row.height}</Text>
                      </View>
                      {row.values.map((val, i) => (
                        <View key={i} style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                          <Text style={{ 
                            fontSize: 10, 
                            color: val ? 'rgba(201,169,110,0.8)' : 'transparent',
                            fontWeight: val ? '400' : '300',
                          }}>{val || '-'}</Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </ScrollView>

              {/* 底部提示 */}
              <View style={{
                padding: 12,
                borderTopWidth: 1,
                borderTopColor: 'rgba(255,255,255,0.08)',
                backgroundColor: 'rgba(201,169,110,0.05)',
              }}>
                <Text style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.35)',
                  textAlign: 'center',
                  fontWeight: '300',
                  lineHeight: 18,
                }}>
                  此表不作为您的决策依据，仅供参考，尽量告知准确测量过的手围
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* 保存设计模态框 */}
        {showSaveModal && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>保存设计</Text>
              <Text style={styles.modalSubtitle}>已选 {bracelet.length} 颗珠子，共 ¥{totalPrice}</Text>
              <View style={styles.modalBraceletPreview}>
                <BraceletPreview beads={bracelet} showLogo={false} />
              </View>
              <TextInput
                style={styles.modalInput}
                placeholder="给设计起个名字"
                placeholderTextColor="rgba(255,255,255,0.3)"
                value={designName}
                onChangeText={setDesignName}
                maxLength={20}
              />
              <View style={styles.modalButtons}>
                <TouchableOpacity
                  style={styles.modalCancelBtn}
                  onPress={() => { setShowSaveModal(false); setDesignName(''); }}
                >
                  <Text style={styles.modalCancelText}>取消</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.modalConfirmBtn}
                  onPress={() => {
                    const name = designName.trim() || `我的设计${new Date().toLocaleDateString()}`;
                    onSaveDesign({
                      name,
                      beadNames: bracelet.map(b => b.name),
                      beadEntries: [...bracelet],
                      price: totalPrice,
                    });
                    setShowSaveModal(false);
                    setDesignName('');
                  }}
                >
                  <Text style={styles.modalConfirmText}>保存</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}

// ==================== 设计广场 ====================

function DesignSquareScreen({
  onNavigate,
}: {
  onNavigate: (page: string) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState('全部');

  const tags = ['全部', '火', '金', '土', '水', '木', '桃花', '事业', '招福', '护身'];

  const filteredDesigns = POPULAR_DESIGNS.filter(design => {
    const matchSearch = !searchQuery || design.name.includes(searchQuery) || design.author.includes(searchQuery);
    const matchTag = activeTag === '全部' || design.tags.includes(activeTag);
    return matchSearch && matchTag;
  });

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* 顶部导航 */}
      <View style={styles.designSquareHeader}>
        <TouchableOpacity onPress={() => onNavigate('home')} style={{ padding: 8 }}>
          <Text style={{ fontSize: 20, color: '#C9A96E' }}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.designSquareHeaderTitle}>设计广场</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* 搜索框 */}
        <View style={styles.designSquareSearch}>
          <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', marginRight: 8 }}>🔍</Text>
          <TextInput
            style={styles.designSquareSearchInput}
            placeholder="搜索设计标题、作者、标签"
            placeholderTextColor="rgba(255,255,255,0.25)"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        {/* 标签筛选 */}
        <View style={styles.designSquareFilter}>
          <Text style={styles.designSquareFilterLabel}>标签筛选</Text>
          <TouchableOpacity>
            <Text style={styles.designSquareFilterMore}>展开</Text>
          </TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingHorizontal: 20, marginBottom: 16 }}>
          {tags.map(tag => (
            <TouchableOpacity
              key={tag}
              style={[styles.designSquareFilterTag, activeTag === tag && styles.designSquareFilterTagActive]}
              onPress={() => setActiveTag(tag)}
            >
              <Text style={[styles.designSquareFilterTagText, activeTag === tag && styles.designSquareFilterTagTextActive]}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* 设计卡片网格 */}
        <View style={[styles.designSquareGrid, { paddingHorizontal: 20 }]}>
          {filteredDesigns.length === 0 && (
            <View style={{ width: '100%', alignItems: 'center', justifyContent: 'center', paddingVertical: 80 }}>
              <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.25)', marginBottom: 8 }}>暂无设计</Text>
              <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.15)' }}>快去 DIY 你的手串吧</Text>
            </View>
          )}
          {filteredDesigns.map(design => (
            <TouchableOpacity key={design.id} style={[styles.designSquareCard, { width: (width - 56) / 2 }]} onPress={() => onNavigate('diy')}>
              <View style={[styles.designSquarePreview, { width: (width - 56) / 2, height: (width - 56) / 2 }]}>
                <View style={{ transform: [{ scale: 0.72 }], marginHorizontal: -41, marginVertical: -41 }}>
                  <BraceletPreview beads={designToPreviewBeads(design)} showLogo={true} />
                </View>
              </View>
              <View style={styles.designSquareInfo}>
                <Text style={styles.designSquareName}>{design.name}</Text>
                <View style={styles.designSquareTags}>
                  {design.tags.slice(0, 3).map((tag, idx) => (
                    <View key={idx} style={styles.designSquareTag}>
                      <Text style={styles.designSquareTagText}>{tag}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.designSquareMeta}>
                  <Text style={styles.designSquareAuthor}>@{design.author}</Text>
                  <Text style={styles.designSquareUseCount}>{design.useCount}次使用</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>

        <View style={{ height: 20 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ==================== 五行水晶设计 ====================

const ELEMENT_COLORS: Record<string, string> = {
  metal: '#C9A96E', wood: '#4CAF50', water: '#2196F3', fire: '#F44336', earth: '#795548',
};
const ELEMENT_BG: Record<string, string> = {
  metal: 'rgba(201,169,110,0.12)', wood: 'rgba(76,175,80,0.12)', water: 'rgba(33,150,243,0.12)', fire: 'rgba(244,67,54,0.12)', earth: 'rgba(121,85,72,0.12)',
};

function WuxingScreen({ onNavigate }: { onNavigate: (page: string) => void }) {
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const activeScenario = WUXING_SCENARIOS.find(s => s.id === selectedScenario);
  const goBack = () => onNavigate('home');

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* 顶部导航 */}
      <View style={styles.designSquareHeader}>
        <TouchableOpacity onPress={goBack} style={{ padding: 8 }}>
          <Text style={{ fontSize: 20, color: '#C9A96E' }}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.designSquareHeaderTitle}>五行水晶设计</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* 五行总览 */}
        <View style={{ paddingHorizontal: 20, marginTop: 16, marginBottom: 20 }}>
          <Text style={{ fontSize: 16, fontWeight: '500', color: '#F5F0E8', marginBottom: 4 }}>金木水火土 · 五行能量</Text>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>相生循环：土生金 → 金生水 → 水生木 → 木生火 → 火生土</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 6 }}>
            {['metal', 'wood', 'water', 'fire', 'earth'].map((key) => {
              const el = WUXING_ELEMENTS[key];
              return (
                <View key={key} style={{ flex: 1, backgroundColor: ELEMENT_BG[key], borderRadius: 10, paddingVertical: 12, alignItems: 'center', borderWidth: 0.5, borderColor: ELEMENT_COLORS[key] + '40' }}>
                  <Text style={{ fontSize: 20, marginBottom: 4, color: ELEMENT_COLORS[key] }}>{el.name}</Text>
                  <Text style={{ fontSize: 9, color: ELEMENT_COLORS[key], lineHeight: 14 }}>{el.trait}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* 六大场景 */}
        <View style={{ paddingHorizontal: 20 }}>
          <Text style={{ fontSize: 15, fontWeight: '500', color: '#F5F0E8', marginBottom: 12 }}>选择你的需求场景</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            {WUXING_SCENARIOS.map(scenario => {
              const isActive = selectedScenario === scenario.id;
              return (
                <TouchableOpacity
                  key={scenario.id}
                  style={{ width: (width - 60) / 2, backgroundColor: isActive ? ELEMENT_BG[scenario.mainElement] : '#1A1A1A', borderRadius: 12, padding: 14, borderWidth: isActive ? 1 : 0.5, borderColor: isActive ? ELEMENT_COLORS[scenario.mainElement] : 'rgba(255,255,255,0.08)' }}
                  onPress={() => setSelectedScenario(isActive ? null : scenario.id)}
                >
                  <Text style={{ fontSize: 22, marginBottom: 6 }}>{scenario.icon}</Text>
                  <Text style={{ fontSize: 14, fontWeight: '500', color: '#F5F0E8', marginBottom: 4 }}>{scenario.name}</Text>
                  <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', lineHeight: 15 }}>{scenario.description}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* 选中场景详情 */}
        {activeScenario && (
          <View style={{ paddingHorizontal: 20, marginTop: 24, marginBottom: 20 }}>
            <View style={{ backgroundColor: '#1A1A1A', borderRadius: 14, padding: 18, borderWidth: 0.5, borderColor: ELEMENT_COLORS[activeScenario.mainElement] + '60' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 14 }}>
                <Text style={{ fontSize: 24, marginRight: 10 }}>{activeScenario.icon}</Text>
                <View>
                  <Text style={{ fontSize: 17, fontWeight: '500', color: '#F5F0E8' }}>{activeScenario.name}</Text>
                  <Text style={{ fontSize: 11, color: ELEMENT_COLORS[activeScenario.mainElement], marginTop: 2 }}>
                    {WUXING_ELEMENTS[activeScenario.mainElement].name}行能量 · {activeScenario.shengRelation}
                  </Text>
                </View>
              </View>

              {/* 设计理念 */}
              <View style={{ backgroundColor: ELEMENT_BG[activeScenario.mainElement], borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 18 }}>{activeScenario.designConcept}</Text>
              </View>

              {/* 主石 */}
              <Text style={{ fontSize: 12, fontWeight: '500', color: '#F5F0E8', marginBottom: 8 }}>主石（60%）· {WUXING_ELEMENTS[activeScenario.mainElement].name}行能量</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                {activeScenario.mainStones.map(stone => (
                  <View key={stone} style={{ backgroundColor: '#0A0A0A', borderRadius: 8, padding: 10, flex: 1, alignItems: 'center', borderWidth: 0.5, borderColor: ELEMENT_COLORS[activeScenario.mainElement] + '30' }}>
                    <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: CRYSTAL_COLORS[stone] ? CRYSTAL_COLORS[stone] + '30' : ELEMENT_COLORS[activeScenario.mainElement] + '20', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
                      <Text style={{ fontSize: 8, color: ELEMENT_COLORS[activeScenario.mainElement], textAlign: 'center' }}>{stone.slice(0, 2)}</Text>
                    </View>
                    <Text style={{ fontSize: 11, color: '#F5F0E8' }}>{stone}</Text>
                  </View>
                ))}
              </View>

              {/* 辅石 */}
              <Text style={{ fontSize: 12, fontWeight: '500', color: '#F5F0E8', marginBottom: 8 }}>辅石（40%）· {WUXING_ELEMENTS[activeScenario.supportElement].name}行能量</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                {activeScenario.supportStones.map(stone => (
                  <View key={stone} style={{ backgroundColor: '#0A0A0A', borderRadius: 8, padding: 10, flex: 1, alignItems: 'center', borderWidth: 0.5, borderColor: ELEMENT_COLORS[activeScenario.supportElement] + '30' }}>
                    <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: ELEMENT_COLORS[activeScenario.supportElement] + '20', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
                      <Text style={{ fontSize: 8, color: ELEMENT_COLORS[activeScenario.supportElement], textAlign: 'center' }}>{stone.slice(0, 2)}</Text>
                    </View>
                    <Text style={{ fontSize: 11, color: '#F5F0E8' }}>{stone}</Text>
                  </View>
                ))}
              </View>

              {/* 适合人群 */}
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>适合人群</Text>
              <View style={{ flexDirection: 'row', gap: 6, marginBottom: 16 }}>
                {activeScenario.targetUsers.map(user => (
                  <View key={user} style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 }}>
                    <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{user}</Text>
                  </View>
                ))}
              </View>

              {/* 一键DIY */}
              <TouchableOpacity style={{ backgroundColor: ELEMENT_COLORS[activeScenario.mainElement], borderRadius: 10, paddingVertical: 12, alignItems: 'center' }} onPress={() => onNavigate('diy')}>
                <Text style={{ fontSize: 14, fontWeight: '500', color: '#0A0A0A' }}>去DIY设计 ›</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* 五行相生图 */}
        <View style={{ paddingHorizontal: 20, marginBottom: 30 }}>
          <Text style={{ fontSize: 15, fontWeight: '500', color: '#F5F0E8', marginBottom: 12 }}>五行相生循环</Text>
          <View style={{ backgroundColor: '#1A1A1A', borderRadius: 14, padding: 20, alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 40, marginBottom: 8 }}>
              <View style={{ alignItems: 'center' }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: ELEMENT_BG.metal, borderWidth: 1, borderColor: ELEMENT_COLORS.metal, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 18, color: ELEMENT_COLORS.metal }}>金</Text>
                </View>
                <Text style={{ fontSize: 9, color: ELEMENT_COLORS.metal, marginTop: 4 }}>肃杀决断</Text>
              </View>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', alignSelf: 'center' }}>→ 金生水 →</Text>
              <View style={{ alignItems: 'center' }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: ELEMENT_BG.water, borderWidth: 1, borderColor: ELEMENT_COLORS.water, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 18, color: ELEMENT_COLORS.water }}>水</Text>
                </View>
                <Text style={{ fontSize: 9, color: ELEMENT_COLORS.water, marginTop: 4 }}>智慧流动</Text>
              </View>
            </View>
            <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.2)', marginBottom: 8 }}>↓</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 40, marginBottom: 8 }}>
              <View style={{ alignItems: 'center' }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: ELEMENT_BG.fire, borderWidth: 1, borderColor: ELEMENT_COLORS.fire, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 18, color: ELEMENT_COLORS.fire }}>火</Text>
                </View>
                <Text style={{ fontSize: 9, color: ELEMENT_COLORS.fire, marginTop: 4 }}>热情光明</Text>
              </View>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', alignSelf: 'center' }}>← 火生土 ←</Text>
              <View style={{ alignItems: 'center' }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: ELEMENT_BG.earth, borderWidth: 1, borderColor: ELEMENT_COLORS.earth, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 18, color: ELEMENT_COLORS.earth }}>土</Text>
                </View>
                <Text style={{ fontSize: 9, color: ELEMENT_COLORS.earth, marginTop: 4 }}>承载包容</Text>
              </View>
            </View>
            <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.2)', marginBottom: 8 }}>↓</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>水生木</Text>
              <View style={{ alignItems: 'center' }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: ELEMENT_BG.wood, borderWidth: 1, borderColor: ELEMENT_COLORS.wood, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 18, color: ELEMENT_COLORS.wood }}>木</Text>
                </View>
                <Text style={{ fontSize: 9, color: ELEMENT_COLORS.wood, marginTop: 4 }}>生发向上</Text>
              </View>
              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>木生火</Text>
            </View>
            <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 16, textAlign: 'center' }}>土生金 → 金生水 → 水生木 → 木生火 → 火生土 · 循环不息</Text>
          </View>
        </View>

        <View style={{ height: 20 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ==================== 个人中心 ====================

function ProfileScreen({
  myDesigns = [],
  onNavigate,
  onShareDesign,
}: {
  myDesigns: Array<{ id: number; name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number; createdAt: string }>;
  onNavigate: (page: string) => void;
  onShareDesign?: (design: { name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number }) => void;
}) {
  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView style={styles.container}>
        {/* 用户信息 */}
        <View style={styles.profileHeader}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>沐</Text>
          </View>
          <View style={styles.userInfo}>
            <Text style={styles.userName}>韩一珠用户</Text>
            <Text style={styles.userId}>ID: HYZ20260001</Text>
          </View>
          <TouchableOpacity style={styles.settingsBtn}>
            <Text style={{ fontSize: 20 }}>⚙</Text>
          </TouchableOpacity>
        </View>

        {/* 统计数据 */}
        <View style={styles.stats}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{myDesigns.length}</Text>
            <Text style={styles.statLabel}>我的设计</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>1</Text>
            <Text style={styles.statLabel}>我的订单</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>2</Text>
            <Text style={styles.statLabel}>优惠券</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>128</Text>
            <Text style={styles.statLabel}>积分</Text>
          </View>
        </View>

        {/* 快捷入口 - 我的设计 */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>我的设计</Text>
            <TouchableOpacity onPress={() => onNavigate('diy')}>
              <Text style={styles.sectionMore}>+ 新建设计</Text>
            </TouchableOpacity>
          </View>

          {myDesigns.length === 0 ? (
            <View style={styles.emptyDesignSmall}>
              <Text style={styles.emptyDesignSmallText}>暂无设计，去DIY创建吧</Text>
            </View>
          ) : (
            myDesigns.map(design => (
              <View key={design.id} style={styles.designCardSmall}>
                {/* 预览区 */}
                <View style={styles.designCardSmallPreview}>
                  <BraceletPreview beads={designToPreviewBeads(design)} showLogo={false} />
                </View>
                {/* 信息区 */}
                <View style={[styles.designCardSmallInfo, { flexDirection: 'row', alignItems: 'center' }]}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.designCardSmallName}>{design.name}</Text>
                    <Text style={styles.designCardSmallDetail}>{design.beadNames.length} 颗 · ¥{design.price}</Text>
                  </View>
                  {onShareDesign && (
                    <TouchableOpacity
                      style={[styles.designDeleteBtn, { borderColor: '#C9A96E', backgroundColor: 'rgba(201,169,110,0.1)', paddingHorizontal: 8 }]}
                      onPress={() => onShareDesign({ name: design.name, beadNames: design.beadNames, beadEntries: design.beadEntries, price: design.price })}
                    >
                      <Text style={[styles.designDeleteText, { color: '#C9A96E' }]}>分享</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            ))
          )}
        </View>

        {/* 功能菜单 */}
        <View style={styles.menuList}>
          {PROFILE_MENU.map(item => (
            <TouchableOpacity key={item.id} style={styles.menuItem}>
              <Text style={styles.menuIcon}>{item.icon}</Text>
              <Text style={styles.menuLabel}>{item.label}</Text>
              {item.count ? (
                <View style={styles.menuBadge}>
                  <Text style={styles.menuBadgeText}>{item.count}</Text>
                </View>
              ) : null}
              <Text style={styles.menuArrow}>›</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={{ height: 30 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ==================== 分享弹窗组件 ====================
function ShareModal({
  design,
  onClose,
}: {
  design: { name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number } | null;
  onClose: () => void;
}) {
  const shareViewRef = useRef<View>(null);
  const [saving, setSaving] = useState(false);

  const handleShare = async () => {
    if (!shareViewRef.current) return;
    try {
      setSaving(true);

      if (isWeb) {
        // Web 端：提示截图
        alert('在浏览器中请按 Ctrl+Shift+S（或截图工具）保存设计图片 ✨');
        setSaving(false);
        return;
      }

      // Native 端：动态加载，避免影响 Web bundle
      const { captureRef } = require('react-native-view-shot');
      const Sharing = require('expo-sharing');
      const uri = await captureRef(shareViewRef, { format: 'png', quality: 0.9 });
      await Sharing.shareAsync(uri, {
        mimeType: 'image/png',
        dialogTitle: '分享"' + (design?.name || '设计') + '"给好友',
        UTI: 'public.png',
      });
    } catch (e: any) {
      if (e.message && !e.message.includes('cancel')) {
        alert('分享失败：' + e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!design) return null;

  return (
    <View style={styles.modalOverlay}>
      <View style={[styles.modalContent, { padding: 20 }]}>
        <Text style={styles.modalTitle}>分享设计</Text>
        <Text style={styles.modalSubtitle}>{design.name} · ¥{design.price}</Text>

        {/* 手串预览（用于截图） */}
        <View
          ref={shareViewRef}
          style={{
            backgroundColor: '#0A0A0A',
            borderRadius: 20,
            padding: 20,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 240,
          }}
        >
          <BraceletPreview beads={designToPreviewBeads(design)} showLogo={true} />
          <View style={{ marginTop: 12 }}>
            <Text style={{ color: '#C9A96E', fontSize: 12, fontWeight: '300', letterSpacing: 2 }}>
              {design.name}
            </Text>
          </View>
        </View>

        <View style={styles.modalButtons}>
          <TouchableOpacity
            style={[styles.modalCancelBtn, { flex: 1 }]}
            onPress={onClose}
          >
            <Text style={styles.modalCancelText}>取消</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.modalConfirmBtn, { flex: 1 }]}
            onPress={handleShare}
            disabled={saving}
          >
            <Text style={styles.modalConfirmText}>{saving ? '生成中...' : '分享到微信'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

// ==================== 错误边界（Web调试用）====================
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null; errorInfo: React.ErrorInfo | null }
> {
  state = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('=== ErrorBoundary caught ===', error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <View style={{
          flex: 1, backgroundColor: '#1a1a2e', padding: 24,
          justifyContent: 'center', alignItems: 'center',
        }}>
          <Text style={{ color: '#ff3b30', fontSize: 18, fontWeight: 'bold', marginBottom: 12 }}>
            ⚠️ 渲染错误
          </Text>
          <Text style={{ color: '#ff6b6b', fontSize: 14, marginBottom: 8, textAlign: 'center' }}>
            {this.state.error?.message}
          </Text>
          <Text style={{ color: '#aaa', fontSize: 11, marginBottom: 16, textAlign: 'center' }}>
            {this.state.error?.stack?.slice(0, 500)}
          </Text>
          <TouchableOpacity
            style={{ backgroundColor: '#007AFF', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}
            onPress={() => this.setState({ error: null, errorInfo: null })}
          >
            <Text style={{ color: '#fff', fontSize: 14 }}>重试</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

// ==================== 主应用 ====================

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [myDesigns, setMyDesigns] = useState<Array<{
    id: number;
    name: string;
    beadNames: string[];
    beadEntries?: Array<{name: string; size: string}>;
    price: number;
    createdAt: string;
  }>>([]);
  const [nextDesignId, setNextDesignId] = useState(1);
  const [storageLoaded, setStorageLoaded] = useState(false);
  // 分享弹窗状态
  const [sharingDesign, setSharingDesign] = useState<{
    name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number;
  } | null>(null);

  // 从 AsyncStorage 加载设计
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const raw = await AsyncStorage.getItem('@han_yi_zhu_designs');
        if (raw && !cancelled) {
          const data = JSON.parse(raw);
          if (data.designs) setMyDesigns(data.designs);
          if (data.nextId) setNextDesignId(data.nextId);
        }
      } catch (_) {}
      if (!cancelled) setStorageLoaded(true);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // 保存设计到 AsyncStorage
  useEffect(() => {
    if (!storageLoaded) return;
    const save = async () => {
      try {
        await AsyncStorage.setItem('@han_yi_zhu_designs', JSON.stringify({
          designs: myDesigns,
          nextId: nextDesignId,
        }));
      } catch (_) {}
    };
    save();
  }, [myDesigns, nextDesignId, storageLoaded]);

  // 保存设计
  const handleSaveDesign = (design: { name: string; beadNames: string[]; beadEntries: Array<{name: string; size: string}>; price: number }) => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const timeStr = hours + ':' + (minutes < 10 ? '0' + minutes : minutes);
    const createdAt = (now.getMonth() + 1) + '月' + now.getDate() + '日 ' + timeStr;
    const newDesign = {
      id: nextDesignId,
      name: design.name,
      beadNames: design.beadNames,
      beadEntries: design.beadEntries,
      price: design.price,
      createdAt,
    };
    setMyDesigns(prev => [newDesign, ...prev]);
    setNextDesignId(prev => prev + 1);
    alert('设计"' + design.name + '"保存成功！');
  };

  // 删除设计
  const handleDeleteDesign = (id: number) => {
    setMyDesigns(prev => prev.filter(d => d.id !== id));
  };

  // 分享设计
  const handleShareDesign = (design: { name: string; beadNames: string[]; beadEntries?: Array<{name: string; size: string}>; price: number }) => {
    setSharingDesign(design);
  };

  return (
    <View style={styles.mainContainer}>
      <StatusBar style="light" />
      {activeTab === 'home' && (
        <HomeScreen
          onNavigate={setActiveTab}
          myDesigns={myDesigns}
          onDeleteDesign={handleDeleteDesign}
          onShareDesign={handleShareDesign}
        />
      )}
      {activeTab === 'diy' && (
        <DIYScreen onSaveDesign={handleSaveDesign} />
      )}
      {activeTab === 'profile' && (
        <ProfileScreen
          myDesigns={myDesigns}
          onNavigate={setActiveTab}
          onShareDesign={handleShareDesign}
        />
      )}
      {activeTab === 'designsquare' && (
        <DesignSquareScreen onNavigate={setActiveTab} />
      )}
      {activeTab === 'wuxing' && (
        <WuxingScreen onNavigate={setActiveTab} />
      )}

      {/* 分享弹窗 */}
      <ShareModal
        design={sharingDesign}
        onClose={() => setSharingDesign(null)}
      />

      {/* 底部导航 */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'home' && styles.tabItemActive]}
          onPress={() => setActiveTab('home')}
        >
          <Text style={[styles.tabIcon, { color: activeTab === 'home' ? '#D4AF37' : 'rgba(201,169,110,0.5)' }]}>🏠</Text>
          <Text style={[styles.tabLabel, activeTab === 'home' && styles.tabLabelActive]}>首页</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'diy' && styles.tabItemActive]}
          onPress={() => setActiveTab('diy')}
        >
          <Text style={[styles.tabIcon, { color: activeTab === 'diy' ? '#D4AF37' : 'rgba(201,169,110,0.5)' }]}>🎨</Text>
          <Text style={[styles.tabLabel, activeTab === 'diy' && styles.tabLabelActive]}>DIY</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabItem, activeTab === 'profile' && styles.tabItemActive]}
          onPress={() => setActiveTab('profile')}
        >
          <Text style={[styles.tabIcon, { color: activeTab === 'profile' ? '#D4AF37' : 'rgba(201,169,110,0.5)' }]}>👤</Text>
          <Text style={[styles.tabLabel, activeTab === 'profile' && styles.tabLabelActive]}>我的</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ==================== 样式 ====================

const styles = StyleSheet.create({
  // ========== 核心容器 ==========
  mainContainer: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },
  container: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },

  // ========== Header ==========
  header: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
  },
  appTitle: {
    fontSize: 32,
    fontWeight: '200',
    color: '#D4AF37',
    letterSpacing: 8,
    fontFamily: '"STXingkai", "Xingkai SC", "KaiTi", "STKaiti", serif',
  },
  appSubtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 6,
    letterSpacing: 2,
    fontWeight: '300',
  },

  // ========== Banner ==========
  bannerScroll: {
    paddingHorizontal: 20,
    marginTop: 8,
  },
  banner: {
    width: width - 48,
    height: 140,
    borderRadius: 20,
    padding: 28,
    justifyContent: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.6,
    shadowRadius: 16,
    elevation: 12,
  },
  bannerTitle: {
    fontSize: 26,
    fontWeight: '400',
    color: '#0A0A0A',
    letterSpacing: 1,
  },
  bannerSubtitle: {
    fontSize: 15,
    color: 'rgba(10,10,10,0.7)',
    marginTop: 8,
    fontWeight: '400',
  },

  // ========== 公告 ==========
  announcement: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginTop: 16,
    padding: 12,
    backgroundColor: 'rgba(201,169,110,0.08)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.15)',
  },
  announcementIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  announcementText: {
    fontSize: 12,
    color: '#C9A96E',
    flex: 1,
    fontWeight: '400',
  },

  // ========== 快捷入口 ==========
  shortcuts: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 24,
    paddingHorizontal: 20,
  },
  shortcut: {
    alignItems: 'center',
  },
  shortcutIcon: {
    width: 60,
    height: 60,
    borderRadius: 20,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.2)',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.2)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    elevation: 6,
  },
  shortcutLabel: {
    fontSize: 11,
    color: 'rgba(212,175,55,0.85)',
    marginTop: 10,
    fontWeight: '300',
    letterSpacing: 1.5,
  },

  // ========== 优惠券 ==========
  coupon: {
    flexDirection: 'row',
    marginHorizontal: 20,
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    padding: 18,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.25)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 8,
  },
  couponLeft: {
    alignItems: 'center',
    paddingRight: 14,
  },
  couponAmount: {
    fontSize: 28,
    fontWeight: '200',
    color: '#C9A96E',
    letterSpacing: 1,
  },
  couponCondition: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.35)',
    marginTop: 2,
  },
  couponDivider: {
    width: 1,
    height: 40,
    backgroundColor: 'rgba(201,169,110,0.2)',
    marginHorizontal: 14,
  },
  couponRight: {
    flex: 1,
  },
  couponTitle: {
    fontSize: 14,
    fontWeight: '400',
    color: '#FFFFFF',
  },
  couponDate: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    marginTop: 4,
  },
  couponGet: {
    fontSize: 12,
    color: '#C9A96E',
    fontWeight: '500',
    padding: 6,
    borderWidth: 1,
    borderColor: '#C9A96E',
    borderRadius: 8,
  },

  // ========== Section ==========
  section: {
    paddingHorizontal: 20,
    marginTop: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '400',
    color: '#F5F0E8',
    letterSpacing: 2,
  },
  sectionMore: {
    fontSize: 13,
    color: '#C9A96E',
    fontWeight: '400',
  },

  // ========== 设计卡片 ==========
  designCard: {
    backgroundColor: '#1A1A1A',
    borderRadius: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 6,
    overflow: 'hidden',
  },
  designCardPreview: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    backgroundColor: 'rgba(0,0,0,0.3)',
    minHeight: 290,
    overflow: 'visible',
  },
  designInfo: {
    padding: 16,
    paddingTop: 12,
  },
  designName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#F5F0E8',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  designMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  designBeads: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '300',
    marginRight: 12,
  },
  designDate: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '300',
  },
  designBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  designAuthor: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '300',
  },
  designPrice: {
    fontSize: 20,
    fontWeight: '300',
    color: '#C9A96E',
    letterSpacing: 1,
  },
  designLikes: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  designLikesIcon: {
    fontSize: 14,
    color: '#C9A96E',
  },
  designLikesCount: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.35)',
    marginLeft: 6,
    fontWeight: '300',
  },

  // ========== 设计广场卡片 ==========
  designSquareGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  designSquareCard: {
    width: (width - 52) / 2,
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  designSquarePreview: {
    width: (width - 52) / 2,
    height: (width - 52) / 2,
    backgroundColor: 'rgba(0,0,0,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  designSquareInfo: {
    padding: 12,
    paddingTop: 10,
  },
  designSquareName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#F5F0E8',
    marginBottom: 6,
  },
  designSquareTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 4,
  },
  designSquareTag: {
    backgroundColor: 'rgba(201,169,110,0.12)',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  designSquareTagText: {
    fontSize: 10,
    color: 'rgba(201,169,110,0.8)',
    fontWeight: '300',
  },
  designSquareMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  designSquareAuthor: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    fontWeight: '300',
  },
  designSquareUseCount: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '300',
  },

  // ========== 设计广场页面 ==========
  designSquareHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 8,
  },
  designSquareHeaderTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#F5F0E8',
    letterSpacing: 2,
  },
  designSquareSearch: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginTop: 8,
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#1A1A1A',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  designSquareSearchInput: {
    flex: 1,
    fontSize: 14,
    color: '#F5F0E8',
    fontWeight: '300',
  },
  designSquareFilter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  designSquareFilterLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400',
  },
  designSquareFilterMore: {
    fontSize: 14,
    color: '#C9A96E',
    fontWeight: '400',
  },
  designSquareFilterTag: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#1A1A1A',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    marginRight: 8,
  },
  designSquareFilterTagActive: {
    backgroundColor: 'rgba(201,169,110,0.15)',
    borderColor: 'rgba(201,169,110,0.4)',
  },
  designSquareFilterTagText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '300',
  },
  designSquareFilterTagTextActive: {
    color: '#C9A96E',
    fontWeight: '400',
  },

  // ========== 手串预览 ==========
  braceletContainer: {
    width: 290,
    height: 290,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  braceletShadow: {
    position: 'absolute',
    bottom: 12,
    left: '15%',
    right: '15%',
    height: 24,
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.8,
    shadowRadius: 16,
  },
  braceletString: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: 'rgba(201,169,110,0.4)',
    backgroundColor: 'transparent',
  },
  bead: {
    position: 'absolute',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.4,
    shadowRadius: 3,
    elevation: 4,
  },
  beadDrillHole: {
    position: 'absolute',
    alignSelf: 'center',
    top: '50%',
    left: '50%',
    marginTop: -3,
    marginLeft: -3,
  },
  beadMainHighlight: {
    position: 'absolute',
  },
  beadSecondaryHighlight: {
    position: 'absolute',
  },
  beadBottomShadow: {
    position: 'absolute',
  },

  // ========== DIY 页面 ==========
  diyContainer: {
    flex: 1,
    backgroundColor: '#0A0A0A',
    flexDirection: 'column',
  },
  previewArea: {
    backgroundColor: '#1A1A1A',
    paddingBottom: 6,
    paddingTop: 6,
    alignItems: 'center',
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(201,169,110,0.15)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.6,
    shadowRadius: 16,
    elevation: 10,
  },
  previewHint: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 2,
    fontWeight: '300',
    letterSpacing: 1,
  },
  priceBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
    gap: 8,
  },
  priceLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '300',
  },
  priceValue: {
    fontSize: 28,
    fontWeight: '300',
    color: '#C9A96E',
    letterSpacing: 2,
    marginLeft: 4,
  },
  wristCheckBtn: {
    marginLeft: 'auto',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: 'rgba(201,169,110,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.3)',
  },
  wristCheckBtnText: {
    fontSize: 12,
    color: '#C9A96E',
    fontWeight: '400',
    letterSpacing: 0.5,
  },
  actionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 6,
    gap: 8,
  },
  actionBtn: {
    width: 36,
    height: 36,
    backgroundColor: '#1A1A1A',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionBtnText: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: '400',
  },
  wristSizeControl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
  wristLabel: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '300',
  },
  wristButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  wristBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#1A1A1A',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.3)',
  },
  wristBtnText: {
    fontSize: 16,
    fontWeight: '300',
    color: '#C9A96E',
  },

  // ========== 分类标签 ==========
  categoryScroll: {
    paddingHorizontal: 20,
    paddingBottom: 4,
  },
  categoryScrollInline: {
    flex: 1,
    marginLeft: 8,
  },
  categoryTag: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 24,
    backgroundColor: '#1A1A1A',
    marginRight: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  categoryTagInline: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 16,
    backgroundColor: '#1A1A1A',
    marginRight: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  categoryTagActive: {
    backgroundColor: '#C9A96E',
    borderColor: '#C9A96E',
  },
  categoryText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400',
  },
  categoryTextInline: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400',
  },
  categoryTextActive: {
    color: '#0A0A0A',
    fontWeight: '500',
  },

  // ========== 规格 ==========
  sizeBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 4,
  },
  sizeBarLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '300',
    marginRight: 12,
  },
  sizeTag: {
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderRadius: 16,
    backgroundColor: '#1A1A1A',
    marginRight: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  sizeTagActive: {
    backgroundColor: 'transparent',
    borderColor: '#C9A96E',
  },
  sizeText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400',
  },
  sizeTextActive: {
    color: '#C9A96E',
    fontWeight: '500',
  },

  // ========== 珠子列表 ==========
  beadList: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  beadRow: {
    justifyContent: 'space-around',
    marginBottom: 12,
    paddingHorizontal: 8,
  },
  beadItem: {
    alignItems: 'center',
    padding: 8,
    width: (width - 60) / 4,
  },
  beadItemCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.5,
    shadowRadius: 6,
    elevation: 5,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative' as const,
    overflow: 'visible' as const,
  },
  beadItemAccessory: {
    borderRadius: 12,
    height: 48,
    marginTop: 8,
  },
  beadItemGold: {
    backgroundColor: '#FFD700',
  },
  beadItemAccessoryHighlight: {
    position: 'absolute',
    top: 4,
    left: 8,
    width: 40,
    height: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.6)',
  },
  beadItemAccessoryBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: 'rgba(201,169,110,0.9)',
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  beadItemAccessoryBadgeText: {
    fontSize: 8,
    color: '#0A0A0A',
    fontWeight: '600',
  },
  beadItemAccessoryName: {
    color: '#C9A96E',
    fontWeight: '400',
  },
  beadItemGoldRing: {
    position: 'absolute',
    top: -1,
    left: -1,
    width: 66,
    height: 66,
    borderRadius: 33,
    borderWidth: 2,
    borderColor: '#FFD700',
    pointerEvents: 'none' as const,
  },
  beadItemDrillHole: {
    position: 'absolute' as const,
    width: 6,
    height: 6,
    borderRadius: 3,
    alignSelf: 'center',
  },
  beadItemMainHighlight: {
    position: 'absolute' as const,
    top: 6,
    left: 10,
    width: 26,
    height: 22,
    borderRadius: 12,
  },
  beadItemSecondaryHighlight: {
    position: 'absolute' as const,
    bottom: 14,
    right: 12,
    width: 14,
    height: 10,
    borderRadius: 7,
  },
  beadItemBottomShadow: {
    position: 'absolute' as const,
    bottom: 0,
    left: 12,
    width: 40,
    height: 12,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.18)',
    pointerEvents: 'none' as const,
  },
  beadItemRing: {
    position: 'absolute' as const,
    top: -1,
    left: -1,
    width: 66,
    height: 66,
    borderRadius: 33,
    borderWidth: 1.5,
    borderColor: 'rgba(201,169,110,0.35)',
    pointerEvents: 'none' as const,
  },
  beadItemName: {
    fontSize: 11,
    color: 'rgba(245,240,232,0.65)',
    marginTop: 8,
    fontWeight: '300',
  },
  beadItemPrice: {
    fontSize: 12,
    color: '#C9A96E',
    fontWeight: '400',
    marginTop: 3,
  },
  beadItemSizePrice: {
    fontSize: 11,
    color: 'rgba(201,169,110,0.75)',
    fontWeight: '400',
    marginTop: 3,
  },

  // ========== DIY 底部按钮 ==========
  diyBottom: {
    flexDirection: 'row',
    padding: 20,
    gap: 14,
    backgroundColor: '#0A0A0A',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.06)',
  },
  saveBtn: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 16,
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: '#C9A96E',
    alignItems: 'center',
  },
  saveBtnText: {
    fontSize: 15,
    color: '#C9A96E',
    fontWeight: '400',
    letterSpacing: 1,
  },
  orderBtn: {
    flex: 2,
    paddingVertical: 16,
    borderRadius: 16,
    backgroundColor: '#C9A96E',
    alignItems: 'center',
    shadowColor: '#C9A96E',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    elevation: 6,
  },
  orderBtnText: {
    fontSize: 15,
    color: '#0A0A0A',
    fontWeight: '500',
    letterSpacing: 1,
  },

  // ========== 个人中心 ==========
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#1A1A1A',
    borderBottomLeftRadius: 32,
    borderBottomRightRadius: 32,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(201,169,110,0.2)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 8,
  },
  avatar: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: '#0A0A0A',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#C9A96E',
    shadowColor: '#C9A96E',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
  },
  avatarText: {
    fontSize: 28,
    fontWeight: '200',
    color: '#C9A96E',
    letterSpacing: 2,
  },
  userInfo: {
    flex: 1,
    marginLeft: 18,
  },
  userName: {
    fontSize: 20,
    fontWeight: '400',
    color: '#F5F0E8',
    letterSpacing: 1,
  },
  userId: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.3)',
    marginTop: 4,
    fontWeight: '300',
  },
  settingsBtn: {
    padding: 8,
  },

  // ========== 统计 ==========
  stats: {
    flexDirection: 'row',
    backgroundColor: '#1A1A1A',
    margin: 20,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 10,
    elevation: 6,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statDivider: {
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: '300',
    color: '#C9A96E',
    letterSpacing: 1,
  },
  statLabel: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    marginTop: 6,
    fontWeight: '300',
    letterSpacing: 0.5,
  },

  // ========== 菜单 ==========
  menuList: {
    backgroundColor: '#1A1A1A',
    marginHorizontal: 20,
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 10,
    elevation: 6,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 18,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.04)',
  },
  menuIcon: {
    fontSize: 20,
    marginRight: 14,
  },
  menuLabel: {
    flex: 1,
    fontSize: 15,
    color: '#F5F0E8',
    fontWeight: '300',
    letterSpacing: 0.5,
  },
  menuBadge: {
    backgroundColor: '#C9A96E',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginRight: 10,
  },
  menuBadgeText: {
    fontSize: 11,
    color: '#0A0A0A',
    fontWeight: '600',
  },
  menuArrow: {
    fontSize: 18,
    color: 'rgba(255,255,255,0.2)',
  },

  // ========== Tab Bar ==========
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#1A1A1A',
    borderTopWidth: 1,
    borderTopColor: 'rgba(201,169,110,0.15)',
    paddingBottom: 8,
    paddingTop: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 10,
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
  },
  tabItemActive: {},
  tabIcon: {
    fontSize: 22,
  },
  tabLabel: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    marginTop: 4,
    fontWeight: '300',
    letterSpacing: 0.5,
  },
  tabLabelActive: {
    color: '#C9A96E',
    fontWeight: '400',
  },

  // ========== 手串Logo ==========
  braceletLogo: {
    position: 'absolute',
    backgroundColor: '#1A1A1A',
    borderWidth: 1.5,
    borderColor: 'rgba(201,169,110,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#C9A96E',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 5,
  },
  braceletLogoText: {
    color: '#C9A96E',
    fontWeight: '600',
    letterSpacing: 1,
  },
  braceletLogoSubText: {
    color: 'rgba(201,169,110,0.7)',
    fontWeight: '300',
    letterSpacing: 2,
  },

  // ========== 空状态 ==========
  emptyDesign: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#1A1A1A',
    borderRadius: 20,
    marginHorizontal: 20,
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.1)',
  },
  emptyDesignIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyDesignTitle: {
    fontSize: 18,
    color: '#FFFFFF',
    fontWeight: '400',
    marginBottom: 8,
  },
  emptyDesignDesc: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '300',
    marginBottom: 20,
  },
  emptyDesignBtn: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    backgroundColor: '#C9A96E',
    borderRadius: 24,
    shadowColor: '#C9A96E',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 4,
  },
  emptyDesignBtnText: {
    color: '#0A0A0A',
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 1,
  },
  emptyDesignSmall: {
    padding: 20,
    alignItems: 'center',
  },
  emptyDesignSmallText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '300',
  },

  // ========== 小设计卡片 ==========
  designCardSmall: {
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    marginBottom: 12,
    marginHorizontal: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  designCardSmallPreview: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    backgroundColor: 'rgba(0,0,0,0.2)',
    minHeight: 120,
  },
  designCardSmallInfo: {
    padding: 12,
    paddingTop: 8,
  },
  designCardSmallName: {
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: '500',
    marginBottom: 4,
  },
  designCardSmallDetail: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    fontWeight: '300',
  },

  // ========== 设计日期 ==========
  designDate: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    fontWeight: '300',
  },

  // ========== 删除按钮 ==========
  designDeleteBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,59,48,0.15)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,59,48,0.3)',
  },
  designDeleteText: {
    fontSize: 11,
    color: '#ff3b30',
    fontWeight: '400',
  },

  // ========== 模态框 ==========
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    width: width - 60,
    backgroundColor: '#1A1A1A',
    borderRadius: 24,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.2)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.6,
    shadowRadius: 20,
    elevation: 12,
  },
  modalTitle: {
    fontSize: 20,
    color: '#C9A96E',
    fontWeight: '400',
    letterSpacing: 1,
    marginBottom: 8,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: '300',
    textAlign: 'center',
    marginBottom: 16,
  },
  modalBraceletPreview: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalInput: {
    backgroundColor: '#0A0A0A',
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    color: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(201,169,110,0.2)',
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.5)',
    fontWeight: '400',
  },
  modalConfirmBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: '#C9A96E',
    alignItems: 'center',
    shadowColor: '#C9A96E',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 4,
  },
  modalConfirmText: {
    fontSize: 15,
    color: '#0A0A0A',
    fontWeight: '500',
    letterSpacing: 1,
  },
});
